import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import UserMultiSelect from "../components/ui/UserMultiSelect";
import DesignerMultiSelect from "../components/ui/DesignerMultiSelect";
// Use named import for LoadingSpinner if it's exported that way
import { LoadingCard, LoadingSpinner } from "../components/LoadingSpinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CompanyCamCustomerTab from "@/components/customers/CompanyCamCustomerTab"; // Correct path
import { DateTimePicker, TimePicker } from "../components/ui/time-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

// Referral source options
const REFERRAL_SOURCE_OPTIONS = [
  "Yelp",
  "Google",
  "Website",
  "Self Generated",
  "Facebook",
  "Word of Mouth",
  "Other",
  "Yard Sign",
  "Company Vehicles",
  "Previous Customer",
  "Instagram",
  "Facebook/Instagram Ad",
  "Other Contractor",
  "Previous Customer Referral"
];

// Status options (ensure consistency with backend if applicable)
const STATUS_OPTIONS = [
  "New",
  "Contacting",
  "Qualified",
  "Unqualified",
  "Postponed",
  "Chose Different Company",
  "Design Only", // Specific status
  "Contract", // Specific status (might relate to Project)
  "Design and Project", // Specific status
  "Contact Later",
  "Cancelled",
  "VIP" // Or maybe 'Active Customer' if VIP implies ongoing work
];


// Appointment type options
const APPOINTMENT_TYPE_OPTIONS = [
  { value: "initial_appointment", label: "Initial Appointment" },
  { value: "design_consultation", label: "Design Consultation" },
  { value: "follow_up", label: "Follow Up" },
  { value: "zoom_follow_up", label: "Zoom Follow Up" },
  { value: "other", label: "Other" }
];

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Plus,
  Search,
  Phone,
  Mail,
  MapPin,
  Calendar,
  DollarSign,
  User,
  Eye,
  Edit,
  Save,
  Clock,
  MessageSquare,
  Users,
  FileText,
  Trash2,
  Pencil,
  Building,
  AlertTriangle,
} from "lucide-react";
// Import all services
import { customerService } from "../services/customerService";
import { emailService } from "../services/emailService";
import { emailTemplateService } from "../services/emailTemplateService";
import { projectService } from "../services/projectService";
import { designerService } from "../services/designerService";
import { subcontractorService } from "../services/subcontractorService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../hooks/use-toast";
import { parseApiError } from "../utils/errorUtils";
import { useAuth } from "../contexts/AuthContext";
import ProjectDetailDialog from "../components/projects/ProjectDetailDialog";
import PhoneInput from "../components/ui/PhoneInput";
import { formatPhone } from "../utils/phoneUtils";

export default function Customers() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Check if user has edit permissions (not a basic 'user' role)
  const canEdit = user?.role !== 'user';
  const canDelete = user?.role === 'admin';
  
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);

  // New customer management dialogs
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [isContactLogDialogOpen, setIsContactLogDialogOpen] = useState(false);
  const [isAppointmentDialogOpen, setIsAppointmentDialogOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null); // For editing appointments
  const [editingAppointmentOriginalTitle, setEditingAppointmentOriginalTitle] = useState(null); // Track original title for API call
  const [editingContactLog, setEditingContactLog] = useState(null); // For editing contact logs
  const [editingContactLogIndex, setEditingContactLogIndex] = useState(null); // Track index for API call
  
  // Appointment email preview state
  const [appointmentEmailPreview, setAppointmentEmailPreview] = useState(null);
  const [isAppointmentEmailDialogOpen, setIsAppointmentEmailDialogOpen] = useState(false);
  const [sendingAppointmentEmail, setSendingAppointmentEmail] = useState(false);
  
  // Delete confirmation dialogs
  const [isDeleteNoteDialogOpen, setIsDeleteNoteDialogOpen] = useState(false);
  const [deletingNoteIndex, setDeletingNoteIndex] = useState(null);
  const [isDeleteContactLogDialogOpen, setIsDeleteContactLogDialogOpen] = useState(false);
  const [deletingContactLogIndex, setDeletingContactLogIndex] = useState(null);
  const [isDeleteAppointmentDialogOpen, setIsDeleteAppointmentDialogOpen] = useState(false);
  const [deletingAppointmentTitle, setDeletingAppointmentTitle] = useState(null);

  // Project detail dialog state
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [isProjectDetailOpen, setIsProjectDetailOpen] = useState(false);

  const [newCustomer, setNewCustomer] = useState({
    name: "", // First Name
    email: "",
    phone: "",
    address: "",
    referral_source: "",
    status: "New",
    initial_note: "",
    preferences: [],
    assigned_user_ids: [], // User assignment
    company: "", // "Exteriors" or "Landscaping"
    // New fields
    last_name: "",
    company_account: "",
    description: "",
    spouse_phone: "",
    spouse_email: "",
    spouse: "",
    hoa: false,
    gate_code: "",
    first_email_date: "",
    first_call_date: "",
    city: "",
    zip_code: "",
    appointment_date: "",
    budget: ""
  });

  // New functionality states
  const [newNote, setNewNote] = useState("");
  const [newContactLog, setNewContactLog] = useState({
    contact_type: "phone",
    contact_method: "outbound",
    summary: ""
  });
  const [newAppointment, setNewAppointment] = useState({
    title: "",
    description: "",
    scheduled_date: "",
    scheduled_time: "",
    appointment_type: "initial_appointment"
  });
  const [newPreference, setNewPreference] = useState("");

  // Send Email states
  const [isSendEmailDialogOpen, setIsSendEmailDialogOpen] = useState(false);
  const [emailData, setEmailData] = useState({
    template_id: "",
    to_email: "",
    to_name: "",
    subject: "",
    body: "",
    variables: {}
  });
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState(null);

  // Create Project state
  const [isCreateProjectDialogOpen, setIsCreateProjectDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState({
    name: "",
    customer_id: "",
    jobsite_address: "",
    city: "",
    zip: "",
    project_type: "construction",
    priority: "medium",
    contract_sign_date: "",
    start_date: "",
    end_date: "",
    contract_price: "",
    estimated_construction_budget: "",
    description: "",
    designer_ids: [],
    subcontractor_ids: []
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const customersPerPage = 25;

  // Queries
  const { data: customersData, isLoading: loading } = useQuery({
    queryKey: ['customers', searchTerm, currentPage],
    queryFn: () => customerService.getAll({
      search: searchTerm || undefined,
      page: currentPage,
      limit: customersPerPage
    })
  });

  const { data: designersData } = useQuery({
    queryKey: ['designers'],
    queryFn: () => designerService.getAll()
  });

  const { data: subcontractorsData } = useQuery({
    queryKey: ['subcontractors'],
    queryFn: () => subcontractorService.getAll()
  });

  const designers = designersData?.data || [];
  const subcontractors = subcontractorsData?.data || [];

  const location = useLocation();

  // Handle opening customer from search (via query parameter)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openId = params.get('openId');

    if (openId) {
      // Directly fetch the customer by ID and open dialog
      const fetchAndOpen = async () => {
        try {
          console.log(`Attempting to fetch customer details for ID: ${openId}`);
          const response = await customerService.getById(openId);
          console.log('Fetched customer details:', response.data);
          setSelectedCustomer(response.data);
          setIsViewDialogOpen(true);
          // Clear the query parameter from URL after handling
          window.history.replaceState({}, '', '/customers');
        } catch (error) {
          console.error("Failed to fetch customer details:", error);
          toast({
            variant: "destructive",
            description: "Failed to load customer details"
          });
          // Clear the query parameter from URL
          window.history.replaceState({}, '', '/customers');
        }
      };
      fetchAndOpen();
    }
  }, [location.search]); // Only depend on location.search

  // Mutations
  const createCustomerMutation = useMutation({
    mutationFn: (data) => customerService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast({ description: "Customer created successfully!" });
      setNewCustomer({
        name: "",
        email: "",
        phone: "",
        address: "",
        referral_source: "",
        status: "New",
        initial_note: "",
        preferences: [],
        assigned_user_ids: [],
        last_name: "",
        company_account: "",
        description: "",
        spouse_phone: "",
        spouse_email: "",
        spouse: "",
        hoa: false,
        gate_code: "",
        first_email_date: "",
        first_call_date: "",
        city: "",
        zip_code: "",
        appointment_date: "",
        budget: ""
      });
      setIsDialogOpen(false);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to create customer"
      });
    }
  });

  const updateCustomerMutation = useMutation({
    mutationFn: ({ id, data }) => customerService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast({ description: "Customer updated successfully!" });
      setIsEditDialogOpen(false);
      setEditingCustomer(null);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to update customer"
      });
    }
  });

  const addNoteMutation = useMutation({
    mutationFn: ({ id, note }) => customerService.addNote(id, { content: note }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customer-projects'] });
      toast({ description: "Note added successfully!" });
      setNewNote("");
      setIsNoteDialogOpen(false);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to add note"
      });
    }
  });

  const deleteNoteMutation = useMutation({
    mutationFn: ({ id, noteIndex }) => customerService.deleteNote(id, noteIndex),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customer-projects'] });
      toast({ description: "Note deleted successfully!" });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to delete note"
      });
    }
  });

  const addContactLogMutation = useMutation({
    mutationFn: ({ id, contactLog }) => customerService.addContactLog(id, contactLog),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customer-projects'] });
      toast({ description: "Contact log added successfully!" });
      setNewContactLog({
        contact_type: "phone",
        contact_method: "outbound",
        summary: ""
      });
      setIsContactLogDialogOpen(false);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to add contact log"
      });
    }
  });

  const updateContactLogMutation = useMutation({
    mutationFn: ({ id, logIndex, contactLog }) => customerService.updateContactLog(id, logIndex, contactLog),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast({ description: "Contact log updated successfully!" });
      setEditingContactLog(null);
      setEditingContactLogIndex(null);
      setIsContactLogDialogOpen(false);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to update contact log"
      });
    }
  });

  const deleteContactLogMutation = useMutation({
    mutationFn: ({ id, logIndex }) => customerService.deleteContactLog(id, logIndex),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast({ description: "Contact log deleted successfully!" });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to delete contact log"
      });
    }
  });

  const addAppointmentMutation = useMutation({
    mutationFn: ({ id, appointment }) => customerService.addAppointment(id, appointment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customer-projects'] });
      toast({ description: "Appointment added successfully!" });
      setNewAppointment({
        title: "",
        description: "",
        scheduled_date: "",
        scheduled_time: "",
        appointment_type: "initial_appointment"
      });
      setIsAppointmentDialogOpen(false);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to add appointment"
      });
    }
  });

  const updateAppointmentMutation = useMutation({
    mutationFn: ({ id, appointmentTitle, appointment }) => customerService.updateAppointment(id, appointmentTitle, appointment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast({ description: "Appointment updated successfully!" });
      setEditingAppointment(null);
      setEditingAppointmentOriginalTitle(null);
      setIsAppointmentDialogOpen(false);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to update appointment"
      });
    }
  });

  const deleteAppointmentMutation = useMutation({
    mutationFn: ({ id, appointmentTitle }) => customerService.deleteAppointment(id, appointmentTitle),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast({ description: "Appointment deleted successfully!" });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to delete appointment"
      });
    }
  });

  const sendEmailMutation = useMutation({
    mutationFn: (data) => emailService.send(data),
    onSuccess: (response) => {
      const warnings = response?.data?.warnings || response?.warnings || [];
      if (warnings.length > 0) {
        toast({ description: `Email sent! Warning: ${warnings.join('; ')}`, duration: 8000 });
      } else {
        toast({ description: "Email sent successfully!" });
      }
      setIsSendEmailDialogOpen(false);
      setEmailData({
        template_id: "",
        to_email: "",
        to_name: "",
        subject: "",
        body: "",
        variables: {}
      });
      setSelectedEmailTemplate(null);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to send email"
      });
    }
  });

  const createProjectMutation = useMutation({
    mutationFn: (data) => projectService.create(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['customer-projects'] });
      toast({ description: "Project created successfully!" });
      setIsCreateProjectDialogOpen(false);
      
      // Store new project ID for auto-navigation
      const newProjectId = data?.data?.id || data?.data?._id;
      if (newProjectId) {
        sessionStorage.setItem('newProjectId', newProjectId);
        // Navigate to projects page - the Projects component will handle opening the detail dialog
        navigate('/projects');
      }
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: parseApiError(error, "Failed to create project")
      });
    }
  });

  // Fetch email templates for sending (only customer templates)
  const { data: templatesData } = useQuery({
    queryKey: ['email-templates', 'customer'],
    queryFn: () => emailTemplateService.getAll({
      category: 'customer',
      active_only: true
    })
  });

  // Fetch projects for selected customer when dialog opens
  const customerIdForProjects = selectedCustomer?.id || selectedCustomer?._id;
  const { data: customerProjectsData, isLoading: loadingProjects } = useQuery({
    queryKey: ['customer-projects', customerIdForProjects],
    queryFn: () => projectService.getAll({ customer_id: customerIdForProjects }),
    enabled: !!customerIdForProjects
  });

  // Fetch full project details when a project is selected for viewing
  const { data: selectedProjectData, isLoading: loadingProjectDetails } = useQuery({
    queryKey: ['project-detail-customer', selectedProjectId],
    queryFn: async () => {
      const response = await projectService.getById(selectedProjectId);
      return response.data;
    },
    enabled: !!selectedProjectId && isProjectDetailOpen
  });


  // Reset page when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);


  const customers = customersData?.data || [];
  const customerProjects = customerProjectsData?.data || []; // Ensure it's always an array


  const handleAddCustomer = async () => {
    // Clean up the data before sending - convert empty strings to undefined for optional fields
    const customerData = {
      name: newCustomer.name, // First Name
      email: newCustomer.email || undefined,
      phone: newCustomer.phone || undefined,
      address: newCustomer.address || undefined,
      referral_source: newCustomer.referral_source || undefined,
      status: newCustomer.status,
      initial_note: newCustomer.initial_note || undefined,
      preferences: newCustomer.preferences || [],
      company: newCustomer.company || undefined,
      // Only include date fields if they have values
      ...(newCustomer.first_email_date && { first_email_date: newCustomer.first_email_date }),
      ...(newCustomer.first_call_date && { first_call_date: newCustomer.first_call_date }),
      ...(newCustomer.appointment_date && { appointment_date: newCustomer.appointment_date }),
      // Other optional fields
      last_name: newCustomer.last_name || undefined,
      company_account: newCustomer.company_account || undefined,
      description: newCustomer.description || undefined,
      spouse: newCustomer.spouse || undefined,
      spouse_phone: newCustomer.spouse_phone || undefined,
      spouse_email: newCustomer.spouse_email || undefined,
      hoa: newCustomer.hoa,
      gate_code: newCustomer.gate_code || undefined,
      city: newCustomer.city || undefined,
      zip_code: newCustomer.zip_code || undefined,
      budget: newCustomer.budget || undefined
    };

    createCustomerMutation.mutate(customerData);
  };


  const getInitials = (name) => {
    if (!name) return "?";
    // Improved initials logic for multiple names
    const names = name.trim().split(' ');
    if (names.length === 1) return names[0][0]?.toUpperCase() || '?';
    return (names[0][0] + (names.length > 1 ? names[names.length - 1][0] : '')).toUpperCase();
  };


  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-400'; // Default gray
    status = status.toLowerCase(); // Normalize status
    switch (status) {
      case 'new': return 'bg-blue-500';
      case 'contacting': return 'bg-yellow-500';
      case 'qualified': return 'bg-green-500';
      case 'unqualified': return 'bg-red-500';
      case 'postponed': return 'bg-orange-400';
      case 'chose different company': return 'bg-gray-500';
      case 'design only': return 'bg-purple-500';
      case 'contract': return 'bg-teal-500'; // Example color
      case 'design and project': return 'bg-indigo-500'; // Example color
      case 'contact later': return 'bg-yellow-300 text-black'; // Example color
      case 'cancelled': return 'bg-red-700';
      case 'vip': return 'bg-pink-500'; // Example color
      case 'active customer': return 'bg-green-600'; // If you use this status
      default: return 'bg-gray-400';
    }
  };


  // Customer management handlers
  const handleEditCustomer = (customer) => {
    setEditingCustomer({
      ...customer,
      // Ensure date fields are formatted correctly for input type="date"
      first_email_date: customer.first_email_date ? customer.first_email_date.split('T')[0] : "",
      first_call_date: customer.first_call_date ? customer.first_call_date.split('T')[0] : "",
      // Pass full ISO string to DateTimePicker - it handles timezone conversion
      appointment_date: customer.appointment_date || "",
      preferences: customer.preferences || [] // Ensure preferences is always an array
    });
    setIsEditDialogOpen(true);
  };


  const handleUpdateCustomer = async () => {
    if (!editingCustomer) return;
    
    const updateData = {
      name: editingCustomer.name,
      email: editingCustomer.email,
      phone: editingCustomer.phone,
      address: editingCustomer.address,
      referral_source: editingCustomer.referral_source,
      status: editingCustomer.status,
      company: editingCustomer.company,
      preferences: editingCustomer.preferences || [],
      assigned_user_ids: editingCustomer.assigned_user_ids || [],
      last_name: editingCustomer.last_name,
      company_account: editingCustomer.company_account,
      description: editingCustomer.description,
      spouse_phone: editingCustomer.spouse_phone,
      spouse_email: editingCustomer.spouse_email,
      spouse: editingCustomer.spouse,
      hoa: editingCustomer.hoa,
      gate_code: editingCustomer.gate_code,
      // Conditionally include dates only if they have a value
      ...(editingCustomer.first_email_date && { first_email_date: editingCustomer.first_email_date }),
      ...(editingCustomer.first_call_date && { first_call_date: editingCustomer.first_call_date }),
      ...(editingCustomer.appointment_date && { appointment_date: editingCustomer.appointment_date }),
      city: editingCustomer.city,
      zip_code: editingCustomer.zip_code,
      budget: editingCustomer.budget
    };

    updateCustomerMutation.mutate({
      id: editingCustomer.id || editingCustomer._id,
      data: updateData
    });
    
    // Refresh selected customer if viewing
    if (selectedCustomer && (selectedCustomer.id || selectedCustomer._id) === (editingCustomer.id || editingCustomer._id)) {
      setTimeout(async () => {
        try {
          const response = await customerService.getById(editingCustomer.id || editingCustomer._id);
          setSelectedCustomer(response.data);
        } catch (error) {
          console.error("Failed to refresh customer:", error);
        }
      }, 500);
    }
  };


  const [deleteClicks, setDeleteClicks] = React.useState({});

  const handleDeleteCustomer = async (customer) => {
    const customerId = customer.id || customer._id;
    const currentClicks = deleteClicks[customerId] || 0;

    if (currentClicks === 0) {
      console.warn(`⚠️ DELETE WARNING: Click delete again to confirm deletion of "${customer.name}"`);
      setDeleteClicks(prev => ({ ...prev, [customerId]: 1 }));
      // Reset click count after a delay
      setTimeout(() => {
        setDeleteClicks(prev => ({ ...prev, [customerId]: 0 }));
      }, 3000); // Reset after 3 seconds
      return;
    }

    // If clicked again within the timeout
    try {
      console.log(`🗑️ Deleting customer: ${customer.name} (ID: ${customerId})`);
      await customerService.delete(customerId);
       setDeleteClicks(prev => ({ ...prev, [customerId]: 0 })); // Reset immediately after successful delete attempt
      queryClient.invalidateQueries({ queryKey: ['customers'] }); // Refresh the customer list
      // Close view/edit dialog if the deleted customer was open
      if (selectedCustomer && (selectedCustomer.id || selectedCustomer._id) === customerId) {
          setIsViewDialogOpen(false);
          setSelectedCustomer(null);
      }
       if (editingCustomer && (editingCustomer.id || editingCustomer._id) === customerId) {
          setIsEditDialogOpen(false);
          setEditingCustomer(null);
      }
    } catch (error) {
      console.error("❌ Failed to delete customer:", error);
       setDeleteClicks(prev => ({ ...prev, [customerId]: 0 })); // Reset on error too
       // Show error toast/message
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedCustomer) return;
    
    addNoteMutation.mutate({
      id: selectedCustomer.id || selectedCustomer._id,
      note: newNote
    });
    
    // Refresh customer
    setTimeout(async () => {
      try {
        const response = await customerService.getById(selectedCustomer.id || selectedCustomer._id);
        setSelectedCustomer(response.data);
      } catch (error) {
        console.error("Failed to refresh customer:", error);
      }
    }, 500);
  };


  const handleDeleteNote = async (noteIndex) => {
    setDeletingNoteIndex(noteIndex);
    setIsDeleteNoteDialogOpen(true);
  };

  const confirmDeleteNote = async () => {
    if (!selectedCustomer || deletingNoteIndex === null) return;
    
    deleteNoteMutation.mutate({
      id: selectedCustomer.id || selectedCustomer._id,
      noteIndex: deletingNoteIndex
    });
    
    setIsDeleteNoteDialogOpen(false);
    setDeletingNoteIndex(null);
    
    // Refresh customer
    setTimeout(async () => {
      try {
        const response = await customerService.getById(selectedCustomer.id || selectedCustomer._id);
        setSelectedCustomer(response.data);
      } catch (error) {
        console.error("Failed to refresh customer:", error);
      }
    }, 500);
  };


  const handleAddContactLog = async () => {
    if (!newContactLog.summary.trim() || !selectedCustomer) return;
    
    addContactLogMutation.mutate({
      id: selectedCustomer.id || selectedCustomer._id,
      contactLog: newContactLog
    });
    
    // Refresh customer
    setTimeout(async () => {
      try {
        const response = await customerService.getById(selectedCustomer.id || selectedCustomer._id);
        setSelectedCustomer(response.data);
      } catch (error) {
        console.error("Failed to refresh customer:", error);
      }
    }, 500);
  };

  const handleEditContactLog = (log, index) => {
    setEditingContactLog(log);
    setEditingContactLogIndex(index);
    setIsContactLogDialogOpen(true);
  };

  const handleUpdateContactLog = async () => {
    if (!editingContactLog?.summary.trim() || !selectedCustomer) return;
    
    updateContactLogMutation.mutate({
      id: selectedCustomer.id || selectedCustomer._id,
      logIndex: editingContactLogIndex,
      contactLog: editingContactLog
    });
    
    // Refresh customer
    setTimeout(async () => {
      try {
        const response = await customerService.getById(selectedCustomer.id || selectedCustomer._id);
        setSelectedCustomer(response.data);
      } catch (error) {
        console.error("Failed to refresh customer:", error);
      }
    }, 500);
  };

  const handleDeleteContactLog = async (logIndex) => {
    setDeletingContactLogIndex(logIndex);
    setIsDeleteContactLogDialogOpen(true);
  };

  const confirmDeleteContactLog = async () => {
    if (!selectedCustomer || deletingContactLogIndex === null) return;
    
    deleteContactLogMutation.mutate({
      id: selectedCustomer.id || selectedCustomer._id,
      logIndex: deletingContactLogIndex
    });
    
    setIsDeleteContactLogDialogOpen(false);
    setDeletingContactLogIndex(null);
    
    // Refresh customer
    setTimeout(async () => {
      try {
        const response = await customerService.getById(selectedCustomer.id || selectedCustomer._id);
        setSelectedCustomer(response.data);
      } catch (error) {
        console.error("Failed to refresh customer:", error);
      }
    }, 500);
  };


  const handleAddAppointment = async () => {
    if (!newAppointment.title.trim() || !selectedCustomer) return;
    
    addAppointmentMutation.mutate({
      id: selectedCustomer.id || selectedCustomer._id,
      appointment: newAppointment
    });
    
    // Refresh customer
    setTimeout(async () => {
      try {
        const response = await customerService.getById(selectedCustomer.id || selectedCustomer._id);
        setSelectedCustomer(response.data);
      } catch (error) {
        console.error("Failed to refresh customer:", error);
      }
    }, 500);
  };

  const handleEditAppointment = (appointment) => {
    setEditingAppointment(appointment);
    setEditingAppointmentOriginalTitle(appointment.title);
    setIsAppointmentDialogOpen(true);
  };

  const handleUpdateAppointment = async () => {
    if (!editingAppointment?.title.trim() || !selectedCustomer) return;
    
    updateAppointmentMutation.mutate({
      id: selectedCustomer.id || selectedCustomer._id,
      appointmentTitle: editingAppointmentOriginalTitle,
      appointment: editingAppointment
    });
    
    // Refresh customer
    setTimeout(async () => {
      try {
        const response = await customerService.getById(selectedCustomer.id || selectedCustomer._id);
        setSelectedCustomer(response.data);
      } catch (error) {
        console.error("Failed to refresh customer:", error);
      }
    }, 500);
  };

  const handleDeleteAppointment = async (appointmentTitle) => {
    setDeletingAppointmentTitle(appointmentTitle);
    setIsDeleteAppointmentDialogOpen(true);
  };

  const confirmDeleteAppointment = async () => {
    if (!selectedCustomer || !deletingAppointmentTitle) return;
    
    deleteAppointmentMutation.mutate({
      id: selectedCustomer.id || selectedCustomer._id,
      appointmentTitle: deletingAppointmentTitle
    });
    
    setIsDeleteAppointmentDialogOpen(false);
    setDeletingAppointmentTitle(null);
    
    // Refresh customer
    setTimeout(async () => {
      try {
        const response = await customerService.getById(selectedCustomer.id || selectedCustomer._id);
        setSelectedCustomer(response.data);
      } catch (error) {
        console.error("Failed to refresh customer:", error);
      }
    }, 500);
  };

  const handleSendAppointmentConfirmation = async (appointment) => {
    if (!selectedCustomer || !appointment) return;
    
    // Parse the date correctly to avoid timezone issues
    // scheduled_date is stored as "YYYY-MM-DD" string
    let formattedDate = 'TBD';
    if (appointment.scheduled_date) {
      // Split the date string and create date in local timezone
      const dateParts = appointment.scheduled_date.split('-');
      if (dateParts.length === 3) {
        const year = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1; // JS months are 0-indexed
        const day = parseInt(dateParts[2], 10);
        const appointmentDate = new Date(year, month, day);
        formattedDate = appointmentDate.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
    }
    
    // Format time to 12-hour
    let formattedTime = appointment.scheduled_time || 'TBD';
    if (appointment.scheduled_time && !appointment.scheduled_time.includes('AM') && !appointment.scheduled_time.includes('PM')) {
      const [hours, minutes] = appointment.scheduled_time.split(':');
      const hour24 = parseInt(hours, 10);
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const period = hour24 >= 12 ? 'PM' : 'AM';
      formattedTime = `${hour12}:${minutes} ${period}`;
    }
    
    const dateTimeString = `${formattedDate} at ${formattedTime}`;
    
    const customerName = selectedCustomer.name || 'Valued Customer';
    const customerAddress = selectedCustomer.address || '';
    const customerCity = selectedCustomer.city || '';
    const customerZip = selectedCustomer.zip_code || '';
    const locationString = [customerAddress, customerCity, customerZip].filter(Boolean).join(', ') || 'To be confirmed';
    
    const subject = `Appointment Confirmation for ${dateTimeString}`;
    
    const htmlBody = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Appointment Confirmation</title>
</head>
<body style="font-family: Arial, sans-serif; color: #333; background-color: #f9f9f9; margin:0; padding:0;">
  <table align="center" width="600" cellpadding="0" cellspacing="0" 
         style="background-color:#ffffff; border-radius:6px; overflow:hidden; box-shadow:0 2px 6px rgba(0,0,0,0.15);">
    
    <!-- Logo -->
    <tr>
      <td align="center" style="padding:20px;">
        <img src="https://img1.wsimg.com/isteam/ip/0bd1368e-954f-42d8-8ef8-b44c49249554/Reliable%20Logo%20Package%20_Page_1.png/:/rs=w:180,h:140,cg=true,m/cr=w:180,h:140" 
             alt="Reliable Landscaping and Design Logo" style="display:block; max-width:180px; height:auto;">
      </td>
    </tr>
    
    <!-- Body -->
    <tr>
      <td style="padding:30px;">
        <h2 style="color:#2b4d2f; margin-top:0;">Appointment Confirmation</h2>
        
        <p>Hi ${customerName},</p>
        
        <p>This email confirms your upcoming appointment with Reliable Landscaping and Design.</p>
        
        <!-- Appointment Details Box -->
        <table width="100%" cellpadding="12" cellspacing="0" style="background-color:#f0f7f0; border-radius:6px; margin:20px 0; border-left:4px solid #2b4d2f;">
          <tr>
            <td>
              <p style="margin:0 0 8px 0;"><strong>📅 Date:</strong> ${formattedDate}</p>
              <p style="margin:0 0 8px 0;"><strong>🕐 Time:</strong> ${formattedTime}</p>
              <p style="margin:0 0 8px 0;"><strong>📍 Location:</strong> ${locationString}</p>
              <p style="margin:0;"><strong>📋 Appointment Type:</strong> ${appointment.title || 'Consultation'}</p>
            </td>
          </tr>
        </table>
        
        <p>Your appointment time has been reserved specifically for you. If anything changes prior to your visit, please let us know as soon as possible so we can adjust the schedule accordingly.</p>
        
        <p>If you have any questions before your appointment, feel free to reply to this email or call us.</p>
        
        <!-- Call-to-Action Buttons -->
        <center style="margin:30px 0;">
          <a href="mailto:info@reliablelandscapesdesign.com?subject=Appointment%20Question" 
             style="background:#2b4d2f; color:#ffffff; text-decoration:none; padding:12px 20px; border-radius:4px; font-weight:bold; margin-right:10px; display:inline-block;">
             Email Us
          </a>
          <a href="tel:+19169183990" 
             style="background:#4077bb; color:#ffffff; text-decoration:none; padding:12px 20px; border-radius:4px; font-weight:bold; display:inline-block;">
             Call 916-918-3990
          </a>
        </center>
        
        <p>We look forward to seeing you!</p>
        
        <!-- Signature -->
        <center>
          <p style="margin-top:20px;">
            <br>
            <strong>Reliable Landscaping and Design</strong><br>
            📞 <a href="tel:+19169183990" style="color:#2b4d2f; text-decoration:none;">(916) 918-3990</a><br>
            🌐 <a href="https://www.reliablelandscapesdesign.com" style="color:#2b4d2f; text-decoration:none;">www.reliablelandscapesdesign.com</a>
          </p>
        </center>
      </td>
    </tr>
    
    <!-- Footer -->
    <tr>
      <td align="center" style="background:#f0f0f0; padding:12px; font-size:12px; color:#666;">
        © 2025 Reliable Landscaping and Design. All rights reserved.
      </td>
    </tr>
    
  </table>
</body>
</html>
    `;
    
    // Set preview data and open dialog
    setAppointmentEmailPreview({
      to_email: selectedCustomer.email,
      to_name: customerName,
      subject: subject,
      body: htmlBody,
      customer_id: selectedCustomer.id || selectedCustomer._id,
      appointment: appointment
    });
    setIsAppointmentEmailDialogOpen(true);
  };

  const confirmSendAppointmentEmail = async () => {
    if (!appointmentEmailPreview) return;
    
    setSendingAppointmentEmail(true);
    
    try {
      await emailService.send({
        to_email: appointmentEmailPreview.to_email,
        to_name: appointmentEmailPreview.to_name,
        subject: appointmentEmailPreview.subject,
        body: appointmentEmailPreview.body,
        customer_id: appointmentEmailPreview.customer_id,
        is_html: true
      });
      
      // Log the email to activity/contact logs
      try {
        await customerService.addContactLog(appointmentEmailPreview.customer_id, {
          contact_type: "email",
          contact_method: "outbound",
          summary: `Sent appointment confirmation email for: ${appointmentEmailPreview.appointment.title} on ${appointmentEmailPreview.appointment.scheduled_date}`
        });
        
        // Refresh customer to show new activity
        const response = await customerService.getById(appointmentEmailPreview.customer_id);
        setSelectedCustomer(response.data);
      } catch (logError) {
        console.error("Failed to log email activity:", logError);
      }
      
      toast({ description: "Appointment confirmation email sent successfully!" });
      setIsAppointmentEmailDialogOpen(false);
      setAppointmentEmailPreview(null);
    } catch (error) {
      console.error("Failed to send appointment confirmation:", error);
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to send appointment confirmation email"
      });
    } finally {
      setSendingAppointmentEmail(false);
    }
  };


  const handleAddPreference = () => {
    if (!newPreference.trim()) return;
    if (editingCustomer) {
      setEditingCustomer(prev => ({
        ...prev,
        preferences: [...(prev.preferences || []), newPreference] // Ensure preferences exists
      }));
    }
    setNewPreference(""); // Clear input after adding
  };


  const handleRemovePreference = (indexToRemove) => {
    if (editingCustomer) {
      setEditingCustomer(prev => ({
        ...prev,
        preferences: (prev.preferences || []).filter((_, index) => index !== indexToRemove)
      }));
    }
  };


  // Email sending handlers
  const handleSendEmail = (customer) => {
    setEmailData({
      template_id: "",
      to_email: customer.email || "",
      to_name: `${customer.name || ''} ${customer.last_name || ''}`.trim() || customer.email, // Use full name or fallback to email
      subject: "",
      body: "",
      variables: {} // Start empty
    });
    setSelectedCustomer(customer); // Keep track of which customer we are emailing
    setSelectedEmailTemplate(null); // Reset selected template
    setIsSendEmailDialogOpen(true);
  };


  const handleTemplateChange = async (templateId) => {
     if (!templateId) {
         setSelectedEmailTemplate(null);
         setEmailData(prev => ({ ...prev, template_id: '', subject: '', body: '', variables: {} }));
         return;
     }
    try {
      // Fetch full template details to get variables and potentially updated content
      const response = await emailTemplateService.getById(templateId);
      const template = response.data;

      setSelectedEmailTemplate(template);
      setEmailData(prev => ({
        ...prev,
        template_id: templateId,
        subject: template?.subject || "",
        body: template?.body || "",
        // Pre-populate variables based on customer? Maybe not here, backend handles merge.
        variables: {} // Keep variables empty for now, backend will merge
      }));
    } catch (error) {
      console.error("Failed to fetch template details:", error);
      // Fallback to basic template data from the initial list fetch if detail fetch fails
      const fallbackTemplate = (templatesData?.data?.templates || []).find(t => (t.id || t._id) === templateId);
      setSelectedEmailTemplate(fallbackTemplate);
      setEmailData(prev => ({
        ...prev,
        template_id: templateId,
        subject: fallbackTemplate?.subject || "",
        body: fallbackTemplate?.body || "",
        variables: {}
      }));
    }
  };

  // Helper function to format time in 12-hour format
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    
    try {
      // Handle different time formats: "HH:MM" (24-hour) or already "H:MM AM/PM"
      if (timeString.includes('AM') || timeString.includes('PM')) {
        return timeString; // Already in 12-hour format
      }
      
      const [hours, minutes] = timeString.split(':');
      if (!hours || !minutes) return timeString;
      
      const hour24 = parseInt(hours, 10);
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const period = hour24 >= 12 ? 'PM' : 'AM';
      
      return `${hour12}:${minutes} ${period}`;
    } catch (error) {
      return timeString;
    }
  };

  // Function to preview email content with variables replaced
  const getPreviewContent = (text) => {
    if (!text) return text;
    
    let result = text;
    const customer = selectedCustomer || {};
    
    // Format appointment_date if present - display in Pacific time
    let formattedAppointmentDate = '';
    if (customer.appointment_date) {
      try {
        const date = new Date(customer.appointment_date);
        formattedAppointmentDate = date.toLocaleString('en-US', {
          timeZone: 'America/Los_Angeles',
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      } catch (e) {
        formattedAppointmentDate = customer.appointment_date;
      }
    }
    
    // Build a variables map from customer data
    const variables = {
      // Customer fields
      name: customer.name || '',
      last_name: customer.last_name || '',
      full_name: `${customer.name || ''} ${customer.last_name || ''}`.trim(),
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      city: customer.city || '',
      zip_code: customer.zip_code || '',
      spouse: customer.spouse || '',
      spouse_phone: customer.spouse_phone || '',
      spouse_email: customer.spouse_email || '',
      status: customer.status || '',
      budget: customer.budget || '',
      service_type: customer.service_type || '',
      referral_source: customer.referral_source || customer.source || '',
      source: customer.source || customer.referral_source || '',
      gate_code: customer.gate_code || '',
      appointment_date: formattedAppointmentDate,
      description: customer.description || '',
      notes: customer.notes || '',
      // Common aliases with customer_ prefix
      customer_name: customer.name || '',
      customer_last_name: customer.last_name || '',
      customer_full_name: `${customer.name || ''} ${customer.last_name || ''}`.trim(),
      customer_email: customer.email || '',
      customer_phone: customer.phone || '',
      customer_address: customer.address || '',
      customer_city: customer.city || '',
      customer_zip_code: customer.zip_code || '',
    };
    
    // Replace variables
    for (const [key, value] of Object.entries(variables)) {
      const placeholder = `{${key}}`;
      if (result.includes(placeholder)) {
        result = result.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value || `[${key}]`);
      }
    }
    
    return result;
  };

  // Check for unresolved variables
  const getUnresolvedVariables = (text) => {
    if (!text) return [];
    const variablePattern = /\{([^}]+)\}/g;
    const matches = text.match(variablePattern) || [];
    return [...new Set(matches)];
  };

  const handleSendEmailSubmit = async () => {
    const sendPayload = {
      ...emailData,
      customer_id: selectedCustomer?.id || selectedCustomer?._id
    };

    // Preview first to check for missing variable data
    try {
      const preview = await emailService.preview(sendPayload);
      const previewData = preview?.data || preview;
      const warnings = previewData?.warnings || [];

      if (warnings.length > 0) {
        const warningMessages = warnings.map(w => w.message || w).join('\n\n');
        toast({
          variant: "destructive",
          title: "Cannot send — missing variable data",
          description: warningMessages,
          duration: 10000
        });
        return;
      }
    } catch (err) {
      console.error("Preview check failed:", err);
      // If preview fails, still allow sending
    }

    sendEmailMutation.mutate(sendPayload);
  };


  const handleOpenCreateProject = (customer) => {
    // Pre-fill the project form with customer data
    setNewProject({
      name: "", // Let user define project name
      customer_id: customer.id || customer._id,
      jobsite_address: customer.address || "",
      city: customer.city || "",
      zip: customer.zip_code ? String(customer.zip_code).split('.')[0] : "", // Remove decimal if present
      project_type: "construction", // Default type
      priority: "medium", // Default priority
      contract_sign_date: "",
      start_date: "",
      end_date: "",
      contract_price: customer.budget || "", // Use customer budget if available
      estimated_construction_budget: "",
      description: "",
      designer_ids: [],
      subcontractor_ids: []
    });
    setIsCreateProjectDialogOpen(true);
  };


  const handleCreateProject = async () => {
    createProjectMutation.mutate({
      ...newProject,
      customer_id: selectedCustomer?.id || selectedCustomer?._id,
      contract_price: parseFloat(newProject.contract_price || "0") || 0,
      estimated_construction_budget: newProject.project_type === "design_only" 
        ? parseFloat(newProject.estimated_construction_budget || "0") || 0 
        : undefined
    });
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Customers</h1>
          <p className="text-gray-600 mt-1">Manage your customer relationships and history</p>
        </div>
        {canEdit && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand-600 hover:bg-brand-700 w-full sm:w-auto shrink-0">
                <Plus className="mr-2 h-4 w-4" />
                Add Customer
              </Button>
            </DialogTrigger>
          <DialogContent className="max-h-[100dvh] w-screen md:max-w-4xl md:max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col">
            <div className="sticky top-0 z-10 bg-white border-b p-4 sm:p-6">
              <DialogHeader>
                <DialogTitle>Add New Customer</DialogTitle>
                <DialogDescription>
                  Create a new customer profile with their contact information.
                </DialogDescription>
              </DialogHeader>
            </div>
            {/* ADD CUSTOMER FORM - Lots of fields */}
            <div className="overflow-y-auto flex-1 p-4 sm:p-6 space-y-6">
                 {/* Basic Information Section */}
                 <div className="space-y-4">
                   <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Basic Information</h3>
                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                     <div>
                       <Label htmlFor="name">First Name *</Label>
                       <Input id="name" value={newCustomer.name} onChange={(e) => setNewCustomer({...newCustomer, name: e.target.value})} placeholder="First name" />
                     </div>
                     <div>
                       <Label htmlFor="last_name">Last Name</Label>
                       <Input id="last_name" value={newCustomer.last_name} onChange={(e) => setNewCustomer({...newCustomer, last_name: e.target.value})} placeholder="Last name" />
                     </div>
                     <div>
                       <Label htmlFor="company_account">Company/Account</Label>
                       <Input id="company_account" value={newCustomer.company_account} onChange={(e) => setNewCustomer({...newCustomer, company_account: e.target.value})} placeholder="Company or account name" />
                     </div>
                     <div>
                       <Label htmlFor="status">Status</Label>
                       <Select value={newCustomer.status} onValueChange={(value) => setNewCustomer({...newCustomer, status: value})}>
                         <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                         <SelectContent>{STATUS_OPTIONS.map((option) => (<SelectItem key={option} value={option}>{option}</SelectItem>))}</SelectContent>
                       </Select>
                     </div>
                   </div>
                 </div>

                 {/* Contact Information Section */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Contact Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                       <div>
                         <Label htmlFor="email">Email *</Label>
                         <Input id="email" type="email" value={newCustomer.email} onChange={(e) => setNewCustomer({...newCustomer, email: e.target.value})} placeholder="customer@example.com" />
                       </div>
                       <div>
                         <Label htmlFor="phone">Phone</Label>
                         <PhoneInput id="phone" value={newCustomer.phone} onChange={(val) => setNewCustomer({...newCustomer, phone: val})} placeholder="916.232.2332" />
                       </div>
                    </div>
                  </div>

                 {/* Spouse Information Section */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Spouse Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      <div><Label htmlFor="spouse">Spouse Name</Label><Input id="spouse" value={newCustomer.spouse} onChange={(e) => setNewCustomer({...newCustomer, spouse: e.target.value})} placeholder="Spouse name" /></div>
                      <div><Label htmlFor="spouse_phone">Spouse Phone</Label><PhoneInput id="spouse_phone" value={newCustomer.spouse_phone} onChange={(val) => setNewCustomer({...newCustomer, spouse_phone: val})} placeholder="916.232.2332" /></div>
                      <div><Label htmlFor="spouse_email">Spouse Email</Label><Input id="spouse_email" type="email" value={newCustomer.spouse_email} onChange={(e) => setNewCustomer({...newCustomer, spouse_email: e.target.value})} placeholder="spouse@example.com" /></div>
                    </div>
                  </div>

                 {/* Location Section */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Location</h3>
                    <div className="space-y-4">
                       <div><Label htmlFor="address">Street Address</Label><Input id="address" value={newCustomer.address} onChange={(e) => setNewCustomer({...newCustomer, address: e.target.value})} placeholder="123 Main St" /></div>
                       <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                         <div><Label htmlFor="city">City</Label><Input id="city" value={newCustomer.city} onChange={(e) => setNewCustomer({...newCustomer, city: e.target.value})} placeholder="City" /></div>
                         <div><Label htmlFor="zip_code">Zip Code</Label><Input id="zip_code" value={newCustomer.zip_code} onChange={(e) => setNewCustomer({...newCustomer, zip_code: e.target.value})} placeholder="12345" /></div>
                       </div>
                    </div>
                  </div>

                 {/* Property Information Section */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Property Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex items-center space-x-2"><Checkbox id="hoa" checked={newCustomer.hoa} onCheckedChange={(checked) => setNewCustomer({...newCustomer, hoa: checked})} /><Label htmlFor="hoa">HOA Property</Label></div>
                      <div><Label htmlFor="gate_code">Gate Code</Label><Input id="gate_code" value={newCustomer.gate_code} onChange={(e) => setNewCustomer({...newCustomer, gate_code: e.target.value})} placeholder="Gate access code" /></div>
                    </div>
                  </div>

                 {/* Business Details Section */}
                  <div className="space-y-4">
                     <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Business Details</h3>
                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                           <Label htmlFor="company">Company</Label>
                           <Select value={newCustomer.company || ""} onValueChange={(value) => setNewCustomer({...newCustomer, company: value})}>
                             <SelectTrigger><SelectValue placeholder="Select company" /></SelectTrigger>
                             <SelectContent position="popper" sideOffset={5}>
                               <SelectItem value="Landscaping">Landscaping</SelectItem>
                               <SelectItem value="Exteriors">Exteriors</SelectItem>
                             </SelectContent>
                           </Select>
                        </div>
                        <div>
                           <Label htmlFor="referralSource">Referral Source</Label>
                           <Select onValueChange={(value) => setNewCustomer({...newCustomer, referral_source: value})}>
                             <SelectTrigger><SelectValue placeholder="Select referral source" /></SelectTrigger>
                             <SelectContent>{REFERRAL_SOURCE_OPTIONS.map((option) => (<SelectItem key={option} value={option}>{option}</SelectItem>))}</SelectContent>
                           </Select>
                        </div>
                        <div><Label htmlFor="budget">Budget</Label><Input id="budget" value={newCustomer.budget} onChange={(e) => setNewCustomer({...newCustomer, budget: e.target.value})} placeholder="Budget range or amount" /></div>
                     </div>
                  </div>

                 {/* Important Dates Section */}
                 <div className="space-y-4">
                   <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Important Dates</h3>
                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                     <div><Label htmlFor="first_email_date">First Email Date</Label><Input id="first_email_date" type="date" value={newCustomer.first_email_date} onChange={(e) => setNewCustomer({...newCustomer, first_email_date: e.target.value})} /></div>
                     <div><Label htmlFor="first_call_date">First Call Date</Label><Input id="first_call_date" type="date" value={newCustomer.first_call_date} onChange={(e) => setNewCustomer({...newCustomer, first_call_date: e.target.value})} /></div>
                     <div className="col-span-2"><Label htmlFor="appointment_date">Appointment Date & Time</Label><DateTimePicker id="appointment_date" value={newCustomer.appointment_date} onChange={(value) => setNewCustomer({...newCustomer, appointment_date: value})} /></div>
                   </div>
                 </div>

                 {/* User Assignment Section */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">User Assignment</h3>
                    <UserMultiSelect
                      selectedUserIds={newCustomer.assigned_user_ids || []}
                      onChange={(userIds) => setNewCustomer({...newCustomer, assigned_user_ids: userIds})}
                      label="Assign Users"
                      placeholder="Select users to assign..."
                    />
                  </div>

                 {/* Additional Details Section */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Additional Details</h3>
                    <div className="space-y-4">
                       <div><Label htmlFor="description">Description</Label><Textarea id="description" value={newCustomer.description} onChange={(e) => setNewCustomer({...newCustomer, description: e.target.value})} placeholder="Customer description or notes..." rows={2} /></div>
                       <div><Label htmlFor="initialNote">Initial Notes</Label><Textarea id="initialNote" value={newCustomer.initial_note} onChange={(e) => setNewCustomer({...newCustomer, initial_note: e.target.value})} placeholder="Initial notes about the customer..." rows={3} /></div>
                    </div>
                  </div>
             </div>
            <div className="sticky bottom-0 z-10 bg-white border-t p-4 sm:p-6">
              <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-0">
                <Button variant="outline" className="h-10 w-full sm:w-auto" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleAddCustomer} disabled={createCustomerMutation.isPending || !newCustomer.name || !newCustomer.email} className="h-10 w-full sm:w-auto bg-brand-600 hover:bg-brand-700">
                  {createCustomerMutation.isPending ? "Creating..." : "Add Customer"}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search customers by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Customer Stats */}
       {loading ? (
         <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
           <LoadingCard /><LoadingCard /><LoadingCard /><LoadingCard />
         </div>
       ) : (
       <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
         <Card>
           <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Total Customers</CardTitle><User className="h-4 w-4 text-blue-600" /></CardHeader>
           <CardContent><div className="text-2xl font-bold">{customersData?.pagination?.total || customers.length}</div><p className="text-xs text-muted-foreground">+12% from last month</p></CardContent>
         </Card>
          <Card>
           <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Active Customers</CardTitle><User className="h-4 w-4 text-green-600" /></CardHeader>
           {/* Update this calculation if 'active' status means something different */}
           <CardContent><div className="text-2xl font-bold">{customers.filter(c => c.status && c.status.toLowerCase().includes('active')).length}</div><p className="text-xs text-muted-foreground">Currently engaged</p></CardContent>
         </Card>
         <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Total Revenue</CardTitle><DollarSign className="h-4 w-4 text-brand-600" /></CardHeader>
            <CardContent><div className="text-2xl font-bold">${customers.reduce((sum, c) => sum + (c.total_spent || 0), 0).toLocaleString()}</div><p className="text-xs text-muted-foreground">Lifetime value</p></CardContent>
         </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Avg. Project Value</CardTitle><DollarSign className="h-4 w-4 text-orange-600" /></CardHeader>
            <CardContent><div className="text-2xl font-bold">${customers.length > 0 ? Math.round(customers.reduce((sum, c) => sum + (c.total_spent || 0), 0) / customers.length).toLocaleString() : '0'}</div><p className="text-xs text-muted-foreground">Per customer</p></CardContent>
         </Card>
       </div>
       )}


      {/* Customers Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Customers ({customersData?.pagination?.total || customers.length})</CardTitle>
          <CardDescription>
            Complete list of your customers and their information
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4"><LoadingCard /><LoadingCard /><LoadingCard /></div>
          ) : (
          <>
          {/* Mobile card view */}
          <div className="md:hidden space-y-3">
            {customers.map((customer) => {
              const customerId = customer.id || customer._id;
              return (
                <div key={customerId} className="bg-white border rounded-lg p-4 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-brand-600 flex items-center justify-center text-white font-semibold text-sm">
                        {(customer.name || '?')[0]}{(customer.last_name || '')[0]}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm text-gray-900 truncate">
                          {customer.last_name ? `${customer.name} ${customer.last_name}` : customer.name}
                        </p>
                        {customer.email && <p className="text-xs text-gray-500 truncate">{customer.email}</p>}
                        {customer.phone && <p className="text-xs text-gray-500">{formatPhone(customer.phone)}</p>}
                      </div>
                    </div>
                    <Badge className={`${getStatusColor(customer.status)} text-white px-2 py-0.5 text-xs shrink-0 ml-2`}>
                      {customer.status ? customer.status.charAt(0).toUpperCase() + customer.status.slice(1) : 'Unknown'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      {customer.created_at && <span>Joined {new Date(customer.created_at).toLocaleDateString()}</span>}
                      {customer.company && (
                        <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-white font-bold text-[10px] ${customer.company === "Landscaping" ? "bg-blue-500" : "bg-red-500"}`}>
                          {customer.company === "Landscaping" ? "L" : "E"}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={async () => {
                        try {
                          const response = await customerService.getById(customerId);
                          setSelectedCustomer(response.data);
                          setIsViewDialogOpen(true);
                        } catch (error) {
                          setSelectedCustomer(customer);
                          setIsViewDialogOpen(true);
                        }
                      }} className="h-8 w-8 p-0" title="View Details">
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleSendEmail(customer)} disabled={!customer.email} className="h-8 w-8 p-0" title="Send Email">
                        <Mail className="h-4 w-4" />
                      </Button>
                      {canEdit && (
                        <Button size="sm" variant="ghost" onClick={() => handleEditCustomer(customer)} className="h-8 w-8 p-0" title="Edit Customer">
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop table view */}
          <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Join Date</TableHead>
                <TableHead>Projects</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.map((customer) => {
                  const customerId = customer.id || customer._id; // Use consistent ID
                  return (
                <TableRow key={customerId}>
                  <TableCell>
                    <div className="flex items-center space-x-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={`https://avatar.vercel.sh/${customer.email || customer.name}`} alt={customer.name} />
                        <AvatarFallback>{getInitials(customer.name)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{customer.last_name ? `${customer.last_name}, ${customer.name}` : customer.name}</div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <MapPin className="mr-1 h-3 w-3" />
                           {[customer.address, customer.city, customer.zip_code].filter(Boolean).join(', ') || 'No location'}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="text-sm flex items-center"><Mail className="mr-1 h-3 w-3" />{customer.email || 'No email'}</div>
                      <div className="text-sm flex items-center"><Phone className="mr-1 h-3 w-3" />{customer.phone ? formatPhone(customer.phone) : 'No phone'}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                     <Badge className={`${getStatusColor(customer.status)} text-white px-2 py-0.5 text-xs`}>
                      {customer.status ? customer.status.charAt(0).toUpperCase() + customer.status.slice(1) : 'Unknown'}
                     </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center text-sm"><Calendar className="mr-1 h-3 w-3" />{customer.created_at ? new Date(customer.created_at).toLocaleDateString() : 'N/A'}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-center">
                      <div className="font-medium">{customer.projects_count || 0}</div>
                      <div className="text-xs text-gray-500">projects</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {customer.company ? (
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-white font-bold text-sm ${customer.company === "Landscaping" ? "bg-blue-500" : "bg-red-500"}`}>
                        {customer.company === "Landscaping" ? "L" : "E"}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-1"> {/* Reduced space */}
                      {/* SEND EMAIL BUTTON */}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleSendEmail(customer)}
                        disabled={!customer.email}
                        title="Send Email"
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                      {/* VIEW BUTTON */}
                       <Dialog open={isViewDialogOpen && (selectedCustomer?.id === customerId || selectedCustomer?._id === customerId)} onOpenChange={(open) => {
                           if (!open) { setIsViewDialogOpen(false); setSelectedCustomer(null); } // Clear selection on close
                           else { // Fetch fresh data on open
                                const openCustomerDetails = async () => {
                                   try {
                                        console.log(`👁️‍🗨️ Viewing customer: ${customer.name} (ID: ${customerId})`);
                                        const response = await customerService.getById(customerId);
                                        setSelectedCustomer(response.data);
                                        setIsViewDialogOpen(true); // Ensure dialog stays open if already trying to open
                                    } catch (error) {
                                        console.error("Failed to fetch customer details:", error);
                                        setSelectedCustomer(customer); // Fallback
                                        setIsViewDialogOpen(true);
                                    }
                                };
                                openCustomerDetails();
                           }
                       }}>
                           <DialogTrigger asChild>
                              <Button size="sm" variant="outline" title="View Details">
                                <Eye className="h-4 w-4" />
                              </Button>
                           </DialogTrigger>
                           {/* Dialog Content Rendered Below */}
                        </Dialog>
                      {/* EDIT AND DELETE BUTTONS - Only show for admin/manager */}
                      {canEdit && (
                        <Button size="sm" variant="outline" onClick={() => handleEditCustomer(customer)} title="Edit Customer"><Edit className="h-4 w-4" /></Button>
                      )}
                      {canDelete && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteCustomer(customer)}
                          className={`${(deleteClicks[customerId] || 0) > 0 ? "text-white bg-red-600 hover:bg-red-700" : "text-red-600 hover:text-red-700 hover:bg-red-50"}`}
                          title={(deleteClicks[customerId] || 0) > 0 ? "Click again to confirm deletion" : "Delete customer"}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                  );
              })}
            </TableBody>
          </Table>
          </div>
          </>
          )}

          {/* Pagination Controls */}
           {customersData && customersData.pagination && customersData.pagination.total_pages > 1 && (
             <div className="flex items-center justify-between space-x-2 py-4">
               <div className="text-sm text-muted-foreground">
                 Showing {((customersData.pagination.page - 1) * customersPerPage) + 1} to{" "}
                 {Math.min(customersData.pagination.page * customersPerPage, customersData.pagination.total)} of{" "}
                 {customersData.pagination.total} customers
               </div>
               <div className="flex items-center space-x-2">
                 <Button variant="outline" size="sm" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={!customersData.pagination.has_prev}>Previous</Button>
                 <div className="text-sm">Page {customersData.pagination.page} of {customersData.pagination.total_pages}</div>
                 <Button variant="outline" size="sm" onClick={() => setCurrentPage(prev => prev + 1)} disabled={!customersData.pagination.has_next}>Next</Button>
               </div>
             </div>
           )}
        </CardContent>
      </Card>

      {/* --- VIEW CUSTOMER DIALOG --- */}
      {/* Moved outside the map loop for better structure and performance */}
      <Dialog open={isViewDialogOpen} onOpenChange={(open) => {
          if (!open) { setIsViewDialogOpen(false); setSelectedCustomer(null); }
      }}>
          <DialogContent
            className="max-h-[100dvh] w-screen md:max-w-4xl md:max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            {/* Sticky Header */}
            <div className="sticky top-0 z-10 bg-white border-b p-4 sm:p-6">
              <DialogHeader>
                <DialogTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  {selectedCustomer ? (
                    <>
                    <div className="flex items-center space-x-3 min-w-0">
                      <Avatar className="h-10 w-10 sm:h-12 sm:w-12 flex-shrink-0">
                        <AvatarImage src={`https://avatar.vercel.sh/${selectedCustomer.email || selectedCustomer.name}`} alt={selectedCustomer.name}/>
                        <AvatarFallback>{getInitials(selectedCustomer.name)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="text-lg sm:text-xl font-bold truncate">{selectedCustomer.name} {selectedCustomer.last_name || ''}</div>
                        <div className="text-sm text-muted-foreground font-normal truncate">{selectedCustomer.email}</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 flex-shrink-0">
                      <Button size="sm" variant="outline" className="h-9 text-sm" onClick={() => handleEditCustomer(selectedCustomer)}><Edit className="h-4 w-4 mr-1" />Edit</Button>
                      <Button size="sm" className="h-9 text-sm bg-blue-600 hover:bg-blue-700" onClick={() => setIsNoteDialogOpen(true)}><MessageSquare className="h-4 w-4 mr-1" />Add Note</Button>
                    </div>
                    </>
                  ) : (
                    "Loading Customer..."
                  )}
                </DialogTitle>
              </DialogHeader>
            </div>
            {/* Conditional rendering for loading or customer details */}
            {!selectedCustomer ? (
                <div className="flex justify-center items-center py-10">
                    <LoadingSpinner /> {/* Show spinner if selectedCustomer is null */}
                </div>
            ) : (
             <div className="overflow-y-auto flex-1 min-h-0">
                <Tabs defaultValue="overview" className="w-full flex flex-col h-full">
                  {/* Sticky Tabs */}
                  <div className="sticky top-0 z-[5] bg-white border-b px-4">
                    <div className="overflow-x-auto">
                      <TabsList className="inline-flex w-auto sm:grid sm:w-full sm:grid-cols-5 flex-nowrap whitespace-nowrap">
                        <TabsTrigger value="overview" className="text-xs sm:text-sm px-2.5 sm:px-3">Overview</TabsTrigger>
                        <TabsTrigger value="activity" className="text-xs sm:text-sm px-2.5 sm:px-3">Activity</TabsTrigger>
                        <TabsTrigger value="projects" className="text-xs sm:text-sm px-2.5 sm:px-3">Projects</TabsTrigger>
                        <TabsTrigger value="appointments" className="text-xs sm:text-sm px-2.5 sm:px-3">Appointments</TabsTrigger>
                        <TabsTrigger value="companycam" className="text-xs sm:text-sm px-2.5 sm:px-3">CompanyCam</TabsTrigger>
                      </TabsList>
                    </div>
                  </div>

                  {/* Overview Tab Content */}
                  <TabsContent value="overview" className="space-y-4 p-4 sm:p-6">
                     {/* Company Badge */}
                     {selectedCustomer.company && (
                       <div className="flex items-center gap-2 mb-2">
                         <span className="text-sm text-gray-600">Company:</span>
                         <Badge className={selectedCustomer.company === "Landscaping" ? "bg-blue-500 hover:bg-blue-600" : "bg-red-500 hover:bg-red-600"}>
                           {selectedCustomer.company}
                         </Badge>
                       </div>
                     )}
                     
                     {/* Compact Team Assignment — Users + Designers side by side */}
                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-3 py-2 border rounded-lg bg-blue-50/40">
                       <UserMultiSelect
                         compact
                         label="Assigned Users"
                         placeholder="Assign users..."
                         selectedUserIds={selectedCustomer.assigned_user_ids || []}
                         onChange={async (userIds) => {
                           try {
                             const customerId = selectedCustomer.id || selectedCustomer._id;
                             await customerService.update(customerId, { assigned_user_ids: userIds });
                             setSelectedCustomer({ ...selectedCustomer, assigned_user_ids: userIds });
                           } catch (error) {
                             console.error("Failed to update assigned users:", error);
                             toast({ variant: "destructive", description: "Failed to update assigned users" });
                           }
                         }}
                       />
                       <DesignerMultiSelect
                         compact
                         label="Designers"
                         placeholder="Assign designers..."
                         selectedDesignerIds={selectedCustomer.designer_ids || []}
                         onChange={async (designerIds) => {
                           try {
                             const customerId = selectedCustomer.id || selectedCustomer._id;
                             await customerService.update(customerId, { designer_ids: designerIds });
                             setSelectedCustomer({ ...selectedCustomer, designer_ids: designerIds });
                           } catch (error) {
                             console.error("Failed to update designers:", error);
                             toast({ variant: "destructive", description: "Failed to update designers" });
                           }
                         }}
                       />
                     </div>
                     {/* Row 1: Contact & Account Summary */}
                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Card>
                           <CardHeader><CardTitle className="text-base flex items-center"><User className="h-4 w-4 mr-2" />Contact Information</CardTitle></CardHeader>
                           <CardContent className="space-y-3 text-sm">
                              <div className="flex items-center gap-3"><User className="h-4 w-4 text-gray-400 flex-shrink-0" /><div><div className="text-xs text-muted-foreground uppercase tracking-wide">Name</div><div className="text-sm font-medium">{selectedCustomer.name || 'N/A'}{selectedCustomer.last_name ? ` ${selectedCustomer.last_name}` : ''}</div></div></div>
                              {selectedCustomer.company_account && <div className="flex items-center gap-3"><Building className="h-4 w-4 text-gray-400 flex-shrink-0" /><div><div className="text-xs text-muted-foreground uppercase tracking-wide">Company</div><div className="text-sm font-medium">{selectedCustomer.company_account}</div></div></div>}
                              <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-gray-400 flex-shrink-0" /><div><div className="text-xs text-muted-foreground uppercase tracking-wide">Email</div><div className="text-sm font-medium">{selectedCustomer.email ? <a href={`mailto:${selectedCustomer.email}`} className="text-blue-600 hover:underline">{selectedCustomer.email}</a> : 'N/A'}</div></div></div>
                              <div className="flex items-center gap-3"><Phone className="h-4 w-4 text-gray-400 flex-shrink-0" /><div><div className="text-xs text-muted-foreground uppercase tracking-wide">Phone</div><div className="text-sm font-medium">{selectedCustomer.phone ? <a href={`tel:${selectedCustomer.phone}`} className="text-blue-600 hover:underline">{formatPhone(selectedCustomer.phone)}</a> : 'N/A'}</div></div></div>
                              <div className="flex items-start gap-3"><MapPin className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" /><div><div className="text-xs text-muted-foreground uppercase tracking-wide">Address</div><div className="text-sm font-medium">{selectedCustomer.address || 'N/A'}{selectedCustomer.city || selectedCustomer.zip_code ? `, ${[selectedCustomer.city, selectedCustomer.zip_code].filter(Boolean).join(' ')}` : ''}</div></div></div>
                              <div className="flex items-center gap-3"><Users className="h-4 w-4 text-gray-400 flex-shrink-0" /><div><div className="text-xs text-muted-foreground uppercase tracking-wide">Referral</div><div className="text-sm font-medium">{selectedCustomer.referral_source || 'N/A'}</div></div></div>
                           </CardContent>
                        </Card>
                         <Card>
                           <CardHeader><CardTitle className="text-base flex items-center"><DollarSign className="h-4 w-4 mr-2" />Account Summary</CardTitle></CardHeader>
                           <CardContent className="space-y-2 text-sm">
                              <div className="flex justify-between"><span>Status:</span><Badge className={`${getStatusColor(selectedCustomer.status)} text-white px-2 py-0.5 text-xs`}>{selectedCustomer.status ? selectedCustomer.status.charAt(0).toUpperCase() + selectedCustomer.status.slice(1) : 'Unknown'}</Badge></div>
                              <div className="flex justify-between"><span>Join Date:</span><span>{selectedCustomer.created_at ? new Date(selectedCustomer.created_at).toLocaleDateString() : 'N/A'}</span></div>
                              <div className="flex justify-between"><span>Projects:</span><span className="font-semibold">{selectedCustomer.projects_count || 0}</span></div>
                              <div className="flex justify-between"><span>Total Spent:</span><span className="font-semibold text-green-600">${(selectedCustomer.total_spent || 0).toLocaleString()}</span></div>
                           </CardContent>
                        </Card>
                     </div>
                      {/* Row 2: Additional Details if available */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {selectedCustomer.description && (
                              <Card>
                                  <CardHeader><CardTitle className="text-base flex items-center"><User className="h-4 w-4 mr-2" />Description and Notes</CardTitle></CardHeader>
                                  <CardContent className="space-y-2 text-sm">
                                      {selectedCustomer.description && <div><span className="font-medium">Description:</span><p className="mt-1 text-gray-600">{selectedCustomer.description}</p></div>}
                                  </CardContent>
                              </Card>
                          )}
                           {(selectedCustomer.spouse || selectedCustomer.spouse_phone || selectedCustomer.spouse_email) && (
                              <Card>
                                 <CardHeader><CardTitle className="text-base flex items-center"><Users className="h-4 w-4 mr-2" />Spouse Information</CardTitle></CardHeader>
                                 <CardContent className="space-y-2 text-sm">
                                    {selectedCustomer.spouse && <div className="flex justify-between"><span>Spouse Name:</span><span>{selectedCustomer.spouse}</span></div>}
                                    {selectedCustomer.spouse_phone && <div className="flex items-center gap-3"><Phone className="h-4 w-4 text-gray-400 flex-shrink-0" /><a href={`tel:${selectedCustomer.spouse_phone}`} className="text-sm text-blue-600 hover:underline">{formatPhone(selectedCustomer.spouse_phone)}</a></div>}
                                    {selectedCustomer.spouse_email && <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-gray-400 flex-shrink-0" /><a href={`mailto:${selectedCustomer.spouse_email}`} className="text-sm text-blue-600 hover:underline">{selectedCustomer.spouse_email}</a></div>}
                                 </CardContent>
                              </Card>
                           )}
                           {(selectedCustomer.hoa !== undefined || selectedCustomer.gate_code) && (
                              <Card>
                                 <CardHeader><CardTitle className="text-base flex items-center"><MapPin className="h-4 w-4 mr-2" />Property Information</CardTitle></CardHeader>
                                 <CardContent className="space-y-2 text-sm">
                                    {selectedCustomer.hoa !== undefined && <div className="flex justify-between"><span>HOA Property:</span><Badge className={selectedCustomer.hoa ? "bg-green-500" : "bg-gray-500"}>{selectedCustomer.hoa ? "Yes" : "No"}</Badge></div>}
                                    {selectedCustomer.gate_code && <div className="flex justify-between"><span>Gate Code:</span><span className="font-mono">{selectedCustomer.gate_code}</span></div>}
                                 </CardContent>
                              </Card>
                           )}
                           {(selectedCustomer.first_email_date || selectedCustomer.first_call_date || selectedCustomer.appointment_date) && (
                              <Card>
                                 <CardHeader><CardTitle className="text-base flex items-center"><Calendar className="h-4 w-4 mr-2" />Important Dates</CardTitle></CardHeader>
                                 <CardContent className="space-y-2 text-sm">
                                    {selectedCustomer.first_email_date && <div className="flex justify-between"><span>First Email:</span><span>{new Date(selectedCustomer.first_email_date).toLocaleDateString('en-US', {timeZone: 'America/Los_Angeles'})}</span></div>}
                                    {selectedCustomer.first_call_date && <div className="flex justify-between"><span>First Call:</span><span>{new Date(selectedCustomer.first_call_date).toLocaleDateString('en-US', {timeZone: 'America/Los_Angeles'})}</span></div>}
                                    {selectedCustomer.appointment_date && <div className="flex justify-between"><span>Appointment:</span><span>{new Date(selectedCustomer.appointment_date).toLocaleString('en-US', {timeZone: 'America/Los_Angeles', month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true})}</span></div>}
                                 </CardContent>
                              </Card>
                           )}
                           {selectedCustomer.budget && (
                              <Card>
                                 <CardHeader><CardTitle className="text-base flex items-center"><DollarSign className="h-4 w-4 mr-2" />Budget Information</CardTitle></CardHeader>
                                 <CardContent className="text-sm"><div className="flex justify-between"><span>Budget:</span><span className="font-semibold text-green-600">{selectedCustomer.budget}</span></div></CardContent>
                              </Card>
                           )}
                      </div>
                       {/* Row 3: Preferences & Actions */}
                       <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                           <Card className="sm:col-span-2">
                             <CardHeader><CardTitle className="text-base flex items-center"><FileText className="h-4 w-4 mr-2" />Customer Preferences</CardTitle></CardHeader>
                             <CardContent>
                               <div className="flex flex-wrap gap-2">
                                 {(selectedCustomer.preferences && selectedCustomer.preferences.length > 0) ? (
                                   selectedCustomer.preferences.map((pref, index) => (<Badge key={index} variant="outline">{pref}</Badge>))
                                 ) : (<p className="text-sm text-gray-500">No preferences recorded</p>)}
                               </div>
                             </CardContent>
                           </Card>
                           <Card>
                               <CardHeader><CardTitle className="text-base">Quick Actions</CardTitle></CardHeader>
                               <CardContent className="space-y-3">
                                   <Button className="w-full h-10 bg-brand-600 hover:bg-brand-700" onClick={() => handleOpenCreateProject(selectedCustomer)}><Plus className="h-4 w-4 mr-2" />Create Project</Button>
                                   <Button className="w-full h-10" variant="outline" onClick={() => setIsContactLogDialogOpen(true)}><Phone className="h-4 w-4 mr-2" />Log Contact</Button>
                                   <Button className="w-full h-10" variant="outline" onClick={() => setIsAppointmentDialogOpen(true)}><Calendar className="h-4 w-4 mr-2" />Schedule</Button>
                                   <Button className="w-full h-10" variant="outline" onClick={() => handleSendEmail(selectedCustomer)} disabled={!selectedCustomer.email}><Mail className="h-4 w-4 mr-2" />Send Email</Button>
                               </CardContent>
                           </Card>
                       </div>
                  </TabsContent>

                  {/* Activity Tab Content */}
                  <TabsContent value="activity" className="space-y-4 p-4 sm:p-6">
                      <Card>
                        <CardHeader><CardTitle className="text-base">Recent Activity & Notes</CardTitle></CardHeader>
                        <CardContent>
                           <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                              {/* Notes */}
                              {selectedCustomer.notes && Array.isArray(selectedCustomer.notes) && selectedCustomer.notes.length > 0 ? (
                                selectedCustomer.notes.slice().reverse().map((note, index) => ( // Show newest first
                                  <div key={`note-${index}`} className="border-l-4 border-blue-200 pl-4 py-2 bg-blue-50/50 rounded-r-md">
                                    <div className="flex items-center justify-between mb-1">
                                      <div className="flex items-center space-x-2"><MessageSquare className="h-4 w-4 text-blue-500" /><span className="text-sm font-medium text-blue-700">Note</span>{note.created_at && (<span className="text-xs text-gray-500">{new Date(note.created_at).toLocaleString()}</span>)}</div>
                                      <Button variant="ghost" size="sm" onClick={() => handleDeleteNote(selectedCustomer.notes.length - 1 - index)} disabled={deleteNoteMutation.isPending} className="h-6 w-6 p-0 text-gray-400 hover:text-red-600"><Trash2 className="h-3 w-3" /></Button>
                                    </div>
                                    <p className="text-sm text-gray-700">{note.content}</p>
                                  </div>
                                ))
                              ) : null}
                              {/* Contact Logs */}
                              {selectedCustomer.contact_logs && Array.isArray(selectedCustomer.contact_logs) && selectedCustomer.contact_logs.length > 0 ? (
                                selectedCustomer.contact_logs.slice().reverse().map((log, index) => { // Show newest first
                                  const actualIndex = selectedCustomer.contact_logs.length - 1 - index; // Calculate actual index
                                  return (
                                    <div key={`contact-${index}`} className="border-l-4 border-green-200 pl-4 py-2 bg-green-50/50 rounded-r-md">
                                      <div className="flex items-center justify-between mb-1">
                                        <div className="flex items-center space-x-2">
                                          {log.contact_type === 'email' ? <Mail className="h-4 w-4 text-green-500"/> : <Phone className="h-4 w-4 text-green-500"/>}
                                          <span className="text-sm font-medium text-green-700">{log.contact_type?.toUpperCase()} ({log.contact_method})</span>
                                          {log.created_at && (<span className="text-xs text-gray-500">{new Date(log.created_at).toLocaleString()}</span>)}
                                        </div>
                                        {canEdit && (
                                          <div className="flex items-center space-x-1">
                                            <Button 
                                              variant="ghost" 
                                              size="sm" 
                                              onClick={() => handleEditContactLog(log, actualIndex)} 
                                              className="h-6 w-6 p-0 text-gray-400 hover:text-blue-600"
                                            >
                                              <Pencil className="h-3 w-3" />
                                            </Button>
                                            <Button 
                                              variant="ghost" 
                                              size="sm" 
                                              onClick={() => handleDeleteContactLog(actualIndex)} 
                                              disabled={deleteContactLogMutation.isPending} 
                                              className="h-6 w-6 p-0 text-gray-400 hover:text-red-600"
                                            >
                                              <Trash2 className="h-3 w-3" />
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                      <p className="text-sm text-gray-700">{log.summary}</p>
                                      {/* Display email subject/body preview if it's an email log */}
                                      {log.contact_type === 'email' && log.details && (
                                          <div className="mt-2 text-xs text-gray-500 border-t pt-1">
                                              <p><strong>Subject:</strong> {log.details.subject}</p>
                                              {/* Optionally show a snippet of the body */}
                                              {/* <p><strong>Body:</strong> {log.details.body?.substring(0, 100)}...</p> */}
                                          </div>
                                      )}
                                    </div>
                                  );
                                })
                              ) : null}
                              {/* No Activity Message */}
                              {(!selectedCustomer.notes || selectedCustomer.notes.length === 0) && (!selectedCustomer.contact_logs || selectedCustomer.contact_logs.length === 0) && (
                                <div className="text-center py-8"><MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">No activity recorded yet</p><p className="text-sm text-gray-400">Add notes or log contacts</p></div>
                              )}
                           </div>
                           {/* Action buttons moved outside scroll area */}
                           <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                              <Button onClick={() => setIsContactLogDialogOpen(true)} variant="outline" className="flex-1 h-10"><Phone className="h-4 w-4 mr-2" />Log Contact</Button>
                              <Button onClick={() => setIsNoteDialogOpen(true)} variant="outline" className="flex-1 h-10"><MessageSquare className="h-4 w-4 mr-2" />Add Note</Button>
                              <Button onClick={() => handleSendEmail(selectedCustomer)} disabled={!selectedCustomer.email} className="flex-1 h-10 bg-brand-600 hover:bg-brand-700"><Mail className="h-4 w-4 mr-2" />Send Email</Button>
                            </div>
                        </CardContent>
                      </Card>
                  </TabsContent>

                  {/* Projects Tab Content */}
                   <TabsContent value="projects" className="space-y-4 p-4 sm:p-6">
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-base">Customer Projects</CardTitle>
                             <Button size="sm" className="bg-brand-600 hover:bg-brand-700" onClick={() => handleOpenCreateProject(selectedCustomer)}>
                                <Plus className="h-4 w-4 mr-2" /> Create Project
                             </Button>
                        </CardHeader>
                        <CardContent>
                           {loadingProjects ? (
                              <div className="text-center py-8"><LoadingSpinner /> <p className="text-gray-500 mt-2">Loading projects...</p></div>
                           ) : !customerProjects || customerProjects.length === 0 ? (
                              <div className="text-center py-8"><FileText className="h-12 w-12 text-gray-300 mx-auto mb-2" /><p className="text-gray-500">No projects found</p></div>
                           ) : (
                              <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                                {customerProjects.map((project) => (
                                  <div
                                    key={project.id || project._id}
                                    className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                                    onClick={() => {
                                      setSelectedProjectId(project.id || project._id);
                                      setIsProjectDetailOpen(true);
                                    }}
                                  >
                                    <div className="flex justify-between items-start mb-2">
                                      <h3 className="font-semibold text-blue-600 hover:text-blue-800 text-sm sm:text-base">{project.name}</h3>
                                      <Badge className={`${getStatusColor(project.status)} text-white px-2 py-0.5 text-xs flex-shrink-0 ml-2`}>{project.status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'}</Badge>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs text-gray-600">
                                       <div className="flex items-center"><MapPin className="inline h-3 w-3 mr-1 flex-shrink-0" /><span className="truncate">{project.jobsite_address || project.address || 'N/A'}</span></div>
                                       <div className="flex items-center"><DollarSign className="inline h-3 w-3 mr-1 flex-shrink-0" />{(project.contract_price || 0).toLocaleString()}</div>
                                       <div className="flex items-center"><Calendar className="inline h-3 w-3 mr-1 flex-shrink-0" />{project.start_date ? new Date(project.start_date).toLocaleDateString() : 'N/A'}</div>
                                       <div className="flex items-center"><FileText className="inline h-3 w-3 mr-1 flex-shrink-0" />{project.project_type?.replace('_', ' ') || 'N/A'}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                           )}
                        </CardContent>
                      </Card>
                   </TabsContent>

                  {/* Appointments Tab Content */}
                   <TabsContent value="appointments" className="space-y-4 p-4 sm:p-6">
                     <Card>
                       <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-base">Scheduled Appointments</CardTitle>
                            <Button size="sm" className="bg-brand-600 hover:bg-brand-700" onClick={() => setIsAppointmentDialogOpen(true)}>
                                <Calendar className="h-4 w-4 mr-2" /> Schedule New
                            </Button>
                       </CardHeader>
                       <CardContent>
                          <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                            {selectedCustomer.appointments && Array.isArray(selectedCustomer.appointments) && selectedCustomer.appointments.length > 0 ? (
                              selectedCustomer.appointments.slice().reverse().map((appointment, index) => ( // Show newest first
                                <div key={index} className="border rounded-lg p-4 space-y-2 hover:bg-gray-50">
                                  <div className="flex items-center justify-between gap-2">
                                    <h4 className="font-medium text-sm truncate">{appointment.title}</h4>
                                    <div className="flex items-center space-x-2 flex-shrink-0">
                                      <Badge variant="outline" className="text-xs">{appointment.status || 'Scheduled'}</Badge>
                                      {selectedCustomer.email && (
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          onClick={() => handleSendAppointmentConfirmation(appointment)}
                                          className="h-6 w-6 p-0 text-brand-600 hover:text-brand-700 hover:bg-blue-50"
                                          title="Send appointment confirmation email"
                                        >
                                          <Mail className="h-3 w-3" />
                                        </Button>
                                      )}
                                      {canEdit && (
                                        <>
                                          <Button 
                                            size="sm" 
                                            variant="ghost" 
                                            onClick={() => handleEditAppointment(appointment)}
                                            className="h-6 w-6 p-0"
                                            title="Edit appointment"
                                          >
                                            <Edit className="h-3 w-3" />
                                          </Button>
                                          <Button 
                                            size="sm" 
                                            variant="ghost" 
                                            onClick={() => handleDeleteAppointment(appointment.title)}
                                            className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                            title="Delete appointment"
                                          >
                                            <Trash2 className="h-3 w-3" />
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                                    <div className="flex items-center"><Calendar className="h-3 w-3 mr-1" />{appointment.scheduled_date || 'N/A'}</div>
                                    <div className="flex items-center"><Clock className="h-3 w-3 mr-1" />{formatTime(appointment.scheduled_time)}</div>
                                    <div className="flex items-center"><FileText className="h-3 w-3 mr-1" />{APPOINTMENT_TYPE_OPTIONS.find(o => o.value === appointment.appointment_type)?.label || appointment.appointment_type}</div>
                                  </div>
                                  {appointment.description && (<p className="text-xs text-gray-700 pt-1 border-t mt-1">{appointment.description}</p>)}
                                </div>
                              ))
                            ) : (
                              <div className="text-center py-8"><Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">No appointments scheduled</p></div>
                            )}
                          </div>
                       </CardContent>
                     </Card>
                   </TabsContent>

                    {/* --- COMPANY CAM TAB CONTENT --- */}
                    <TabsContent value="companycam" className="p-4 sm:p-6">
                       {/* Log the ID being passed */}
                       {console.log("🟣 Rendering CompanyCamCustomerTab with ID:", selectedCustomer.id || selectedCustomer._id)}
                       <CompanyCamCustomerTab customerId={selectedCustomer.id || selectedCustomer._id} />
                    </TabsContent>


                </Tabs>
              </div>
            )}
          </DialogContent>
      </Dialog>


      {/* --- Other Dialogs (Edit, Add Note, Contact Log, Appointment, Send Email, Create Project) --- */}
      {/* These should remain outside the main Tabs component for the View Dialog */}

      {/* Edit Customer Dialog */}
       <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
         <DialogContent className="max-h-[100dvh] w-screen md:max-w-4xl md:max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col">
           <div className="sticky top-0 z-10 bg-white border-b p-4 sm:p-6">
             <DialogHeader><DialogTitle>Edit Customer</DialogTitle><DialogDescription>Update customer information.</DialogDescription></DialogHeader>
           </div>
           {editingCustomer && (
              <div className="overflow-y-auto flex-1 p-4 sm:p-6 space-y-4">
                  {/* Edit Form Fields */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Basic Info */}
                     <div className="space-y-2"><Label htmlFor="editName">First Name *</Label><Input id="editName" value={editingCustomer.name || ""} onChange={(e) => setEditingCustomer({...editingCustomer, name: e.target.value})} placeholder="First name"/></div>
                     <div className="space-y-2"><Label htmlFor="editLastName">Last Name</Label><Input id="editLastName" value={editingCustomer.last_name || ""} onChange={(e) => setEditingCustomer({...editingCustomer, last_name: e.target.value})} placeholder="Last name"/></div>
                     <div className="space-y-2"><Label htmlFor="editEmail">Email *</Label><Input id="editEmail" type="email" value={editingCustomer.email || ""} onChange={(e) => setEditingCustomer({...editingCustomer, email: e.target.value})} placeholder="customer@example.com"/></div>
                     <div className="space-y-2"><Label htmlFor="editPhone">Phone</Label><PhoneInput id="editPhone" value={editingCustomer.phone || ""} onChange={(val) => setEditingCustomer({...editingCustomer, phone: val})} placeholder="916.232.2332"/></div>
                     <div className="space-y-2"><Label htmlFor="editStatus">Status</Label>
                        <Select value={editingCustomer.status || "New"} onValueChange={(value) => setEditingCustomer({...editingCustomer, status: value})}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{STATUS_OPTIONS.map((option) => (<SelectItem key={option} value={option}>{option}</SelectItem>))}</SelectContent>
                        </Select>
                     </div>
                     <div className="space-y-2"><Label htmlFor="editCompany">Company</Label>
                        <Select value={editingCustomer.company || ""} onValueChange={(value) => setEditingCustomer({...editingCustomer, company: value})}>
                            <SelectTrigger><SelectValue placeholder="Select company" /></SelectTrigger>
                            <SelectContent position="popper" sideOffset={5}>
                              <SelectItem value="Landscaping">Landscaping</SelectItem>
                              <SelectItem value="Exteriors">Exteriors</SelectItem>
                            </SelectContent>
                        </Select>
                     </div>
                      <div className="col-span-2 space-y-2"><Label htmlFor="editAddress">Address</Label><Input id="editAddress" value={editingCustomer.address || ""} onChange={(e) => setEditingCustomer({...editingCustomer, address: e.target.value})} placeholder="123 Main St, City, State"/></div>
                      <div className="col-span-2 space-y-2"><Label htmlFor="editReferralSource">Referral Source</Label>
                         <Select value={editingCustomer.referral_source || ""} onValueChange={(value) => setEditingCustomer({...editingCustomer, referral_source: value})}>
                             <SelectTrigger><SelectValue placeholder="Select referral source" /></SelectTrigger>
                             <SelectContent>{REFERRAL_SOURCE_OPTIONS.map((option) => (<SelectItem key={option} value={option}>{option}</SelectItem>))}</SelectContent>
                         </Select>
                      </div>
                  </div>
                   {/* Additional Information Section */}
                   <div className="space-y-6 pt-6 border-t">
                      <h3 className="text-lg font-semibold text-gray-900">Additional Information</h3>
                      {/* ... other edit fields ... */}
                       <div className="space-y-4"> {/* Personal Details */}
                           <h4 className="text-md font-medium text-gray-700">Personal Details</h4>
                           <div><Label htmlFor="edit_company_account">Company/Account</Label><Input id="edit_company_account" value={editingCustomer.company_account || ""} onChange={(e) => setEditingCustomer({...editingCustomer, company_account: e.target.value})} placeholder="Company or account name"/></div>
                           <div><Label htmlFor="edit_description">Description</Label><Textarea id="edit_description" value={editingCustomer.description || ""} onChange={(e) => setEditingCustomer({...editingCustomer, description: e.target.value})} placeholder="Customer description or notes..." rows={2}/></div>
                       </div>
                        <div className="space-y-4"> {/* Address Details */}
                           <h4 className="text-md font-medium text-gray-700">Address Details</h4>
                           <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                               <div><Label htmlFor="edit_city">City</Label><Input id="edit_city" value={editingCustomer.city || ""} onChange={(e) => setEditingCustomer({...editingCustomer, city: e.target.value})} placeholder="City"/></div>
                               <div><Label htmlFor="edit_zip_code">Zip Code</Label><Input id="edit_zip_code" value={editingCustomer.zip_code || ""} onChange={(e) => setEditingCustomer({...editingCustomer, zip_code: e.target.value})} placeholder="12345"/></div>
                           </div>
                       </div>
                        <div className="space-y-4"> {/* Spouse Info */}
                           <h4 className="text-md font-medium text-gray-700">Spouse Information</h4>
                           <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                               <div><Label htmlFor="edit_spouse">Spouse Name</Label><Input id="edit_spouse" value={editingCustomer.spouse || ""} onChange={(e) => setEditingCustomer({...editingCustomer, spouse: e.target.value})} placeholder="Spouse name"/></div>
                               <div><Label htmlFor="edit_spouse_phone">Spouse Phone</Label><PhoneInput id="edit_spouse_phone" value={editingCustomer.spouse_phone || ""} onChange={(val) => setEditingCustomer({...editingCustomer, spouse_phone: val})} placeholder="916.232.2332"/></div>
                               <div><Label htmlFor="edit_spouse_email">Spouse Email</Label><Input id="edit_spouse_email" type="email" value={editingCustomer.spouse_email || ""} onChange={(e) => setEditingCustomer({...editingCustomer, spouse_email: e.target.value})} placeholder="spouse@example.com"/></div>
                           </div>
                       </div>
                        <div className="space-y-4"> {/* Property Info */}
                           <h4 className="text-md font-medium text-gray-700">Property Information</h4>
                           <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                              <div className="flex items-center space-x-2"><Checkbox id="edit_hoa" checked={editingCustomer.hoa || false} onCheckedChange={(checked) => setEditingCustomer({...editingCustomer, hoa: checked})} /><Label htmlFor="edit_hoa">HOA Property</Label></div>
                              <div><Label htmlFor="edit_gate_code">Gate Code</Label><Input id="edit_gate_code" value={editingCustomer.gate_code || ""} onChange={(e) => setEditingCustomer({...editingCustomer, gate_code: e.target.value})} placeholder="Gate access code"/></div>
                           </div>
                       </div>
                        <div className="space-y-4"> {/* Important Dates */}
                           <h4 className="text-md font-medium text-gray-700">Important Dates</h4>
                           <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                               <div><Label htmlFor="edit_first_email_date">First Email Date</Label><Input id="edit_first_email_date" type="date" value={editingCustomer.first_email_date || ""} onChange={(e) => setEditingCustomer({...editingCustomer, first_email_date: e.target.value})}/></div>
                               <div><Label htmlFor="edit_first_call_date">First Call Date</Label><Input id="edit_first_call_date" type="date" value={editingCustomer.first_call_date || ""} onChange={(e) => setEditingCustomer({...editingCustomer, first_call_date: e.target.value})}/></div>
                               <div className="col-span-2"><Label htmlFor="edit_appointment_date">Appointment Date & Time</Label><DateTimePicker id="edit_appointment_date" value={editingCustomer.appointment_date || ""} onChange={(value) => setEditingCustomer({...editingCustomer, appointment_date: value})}/></div>
                           </div>
                       </div>
                        <div className="space-y-4"> {/* Budget Info */}
                           <h4 className="text-md font-medium text-gray-700">Budget Information</h4>
                           <div><Label htmlFor="edit_budget">Budget</Label><Input id="edit_budget" value={editingCustomer.budget || ""} onChange={(e) => setEditingCustomer({...editingCustomer, budget: e.target.value})} placeholder="Budget range or amount"/></div>
                       </div>
                   </div>
                   {/* User Assignment Section */}
                   <div className="space-y-4">
                      <h4 className="text-md font-medium text-gray-700">User Assignment</h4>
                      <UserMultiSelect
                        selectedUserIds={editingCustomer.assigned_user_ids || []}
                        onChange={(userIds) => setEditingCustomer({...editingCustomer, assigned_user_ids: userIds})}
                        label="Assign Users"
                        placeholder="Select users to assign..."
                      />
                   </div>

                   {/* Preferences Section */}
                   <div className="space-y-4">
                      <h4 className="text-md font-medium text-gray-700">Customer Preferences</h4>
                      <div className="flex gap-2">
                         <Input value={newPreference} onChange={(e) => setNewPreference(e.target.value)} placeholder="Add preference..." onKeyPress={(e) => e.key === 'Enter' && handleAddPreference()} />
                         <Button onClick={handleAddPreference} size="sm" type="button"><Plus className="h-3 w-3" /></Button> {/* Added type="button" */}
                      </div>
                      <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto border rounded p-1 min-h-[40px]">
                         {(editingCustomer.preferences || []).map((pref, index) => (
                           <Badge key={index} variant="secondary" className="flex items-center gap-1 text-xs">
                             {pref}
                             <button type="button" onClick={() => handleRemovePreference(index)} className="ml-1 hover:text-red-500 font-bold text-xs">×</button>
                           </Badge>
                         ))}
                      </div>
                   </div>
              </div>
           )}
           <div className="sticky bottom-0 z-10 bg-white border-t p-4 sm:p-6">
             <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-0">
               <Button variant="outline" className="h-10 w-full sm:w-auto" onClick={() => {setIsEditDialogOpen(false); setEditingCustomer(null);}}>Cancel</Button>
               <Button onClick={handleUpdateCustomer} disabled={updateCustomerMutation.isPending || !editingCustomer?.name || !editingCustomer?.email} className="h-10 w-full sm:w-auto bg-brand-600 hover:bg-brand-700">
                 {updateCustomerMutation.isPending ? "Updating..." : "Update Customer"}
               </Button>
             </DialogFooter>
           </div>
         </DialogContent>
       </Dialog>


      {/* Add Note Dialog */}
      <Dialog open={isNoteDialogOpen} onOpenChange={setIsNoteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader><DialogTitle>Add Note</DialogTitle><DialogDescription>Add a note to the customer's record.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label htmlFor="noteContent">Note Content *</Label><Textarea id="noteContent" value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Enter note..." rows={4} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNoteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddNote} disabled={addNoteMutation.isPending || !newNote.trim()} className="bg-brand-600 hover:bg-brand-700">{addNoteMutation.isPending ? "Adding..." : "Add Note"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Contact Log Dialog */}
      <Dialog open={isContactLogDialogOpen} onOpenChange={(open) => {
        setIsContactLogDialogOpen(open);
        if (!open) {
          setEditingContactLog(null);
          setEditingContactLogIndex(null);
        }
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingContactLog ? 'Edit Contact Log' : 'Add Contact Log'}</DialogTitle>
            <DialogDescription>{editingContactLog ? 'Update contact log details' : 'Log a communication.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contactType">Type *</Label>
                <Select 
                  value={editingContactLog ? editingContactLog.contact_type : newContactLog.contact_type} 
                  onValueChange={(value) => editingContactLog
                    ? setEditingContactLog({...editingContactLog, contact_type: value})
                    : setNewContactLog({...newContactLog, contact_type: value})
                  }
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="in_person">In Person</SelectItem>
                    <SelectItem value="text">Text</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="contactMethod">Method *</Label>
                <Select 
                  value={editingContactLog ? editingContactLog.contact_method : newContactLog.contact_method} 
                  onValueChange={(value) => editingContactLog
                    ? setEditingContactLog({...editingContactLog, contact_method: value})
                    : setNewContactLog({...newContactLog, contact_method: value})
                  }
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="outbound">Outbound</SelectItem>
                    <SelectItem value="inbound">Inbound</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contactSummary">Summary *</Label>
              <Textarea 
                id="contactSummary" 
                value={editingContactLog ? editingContactLog.summary : newContactLog.summary} 
                onChange={(e) => editingContactLog
                  ? setEditingContactLog({...editingContactLog, summary: e.target.value})
                  : setNewContactLog({...newContactLog, summary: e.target.value})
                } 
                placeholder="Brief summary..." 
                rows={4} 
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsContactLogDialogOpen(false);
              setEditingContactLog(null);
              setEditingContactLogIndex(null);
            }}>Cancel</Button>
            {editingContactLog ? (
              <Button 
                onClick={handleUpdateContactLog} 
                disabled={updateContactLogMutation.isPending || !editingContactLog?.summary.trim()} 
                className="bg-brand-600 hover:bg-brand-700"
              >
                {updateContactLogMutation.isPending ? "Updating..." : "Update"}
              </Button>
            ) : (
              <Button 
                onClick={handleAddContactLog} 
                disabled={addContactLogMutation.isPending || !newContactLog.summary.trim()} 
                className="bg-brand-600 hover:bg-brand-700"
              >
                {addContactLogMutation.isPending ? "Adding..." : "Add Log"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Appointment Dialog */}
      <Dialog open={isAppointmentDialogOpen} onOpenChange={(open) => {
        setIsAppointmentDialogOpen(open);
        if (!open) {
          setEditingAppointment(null);
          setEditingAppointmentOriginalTitle(null);
        }
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingAppointment ? 'Edit Appointment' : 'Schedule Appointment'}</DialogTitle>
            <DialogDescription>{editingAppointment ? 'Update appointment details' : 'Schedule a meeting.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="appointmentTitle">Title *</Label>
              <Input 
                id="appointmentTitle" 
                value={editingAppointment ? editingAppointment.title : newAppointment.title} 
                onChange={(e) => editingAppointment 
                  ? setEditingAppointment({...editingAppointment, title: e.target.value})
                  : setNewAppointment({...newAppointment, title: e.target.value})
                } 
                placeholder="Site consultation..." 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="appointmentType">Type *</Label>
              <Select 
                value={editingAppointment ? editingAppointment.appointment_type : newAppointment.appointment_type} 
                onValueChange={(value) => editingAppointment
                  ? setEditingAppointment({...editingAppointment, appointment_type: value})
                  : setNewAppointment({...newAppointment, appointment_type: value})
                }
              >
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{APPOINTMENT_TYPE_OPTIONS.map(option => (<SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="appointmentDescription">Description</Label>
              <Textarea 
                id="appointmentDescription" 
                value={editingAppointment ? (editingAppointment.description || '') : (newAppointment.description || '')} 
                onChange={(e) => editingAppointment
                  ? setEditingAppointment({...editingAppointment, description: e.target.value})
                  : setNewAppointment({...newAppointment, description: e.target.value})
                } 
                placeholder="Additional details..." 
                rows={3} 
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="appointmentDate">Date *</Label>
                <Input 
                  id="appointmentDate" 
                  type="date" 
                  value={editingAppointment ? editingAppointment.scheduled_date : newAppointment.scheduled_date} 
                  onChange={(e) => editingAppointment
                    ? setEditingAppointment({...editingAppointment, scheduled_date: e.target.value})
                    : setNewAppointment({...newAppointment, scheduled_date: e.target.value})
                  } 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="appointmentTime">Time *</Label>
                <TimePicker 
                  id="appointmentTime" 
                  value={editingAppointment ? editingAppointment.scheduled_time : newAppointment.scheduled_time} 
                  onChange={(value) => editingAppointment
                    ? setEditingAppointment({...editingAppointment, scheduled_time: value})
                    : setNewAppointment({...newAppointment, scheduled_time: value})
                  } 
                  placeholder="Select time"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsAppointmentDialogOpen(false);
              setEditingAppointment(null);
              setEditingAppointmentOriginalTitle(null);
            }}>Cancel</Button>
            {editingAppointment ? (
              <Button 
                onClick={handleUpdateAppointment} 
                disabled={updateAppointmentMutation.isPending || !editingAppointment?.title.trim() || !editingAppointment?.scheduled_date || !editingAppointment?.scheduled_time || !editingAppointment?.appointment_type} 
                className="bg-brand-600 hover:bg-brand-700"
              >
                {updateAppointmentMutation.isPending ? "Updating..." : "Update"}
              </Button>
            ) : (
              <Button 
                onClick={handleAddAppointment} 
                disabled={addAppointmentMutation.isPending || !newAppointment.title.trim() || !newAppointment.scheduled_date || !newAppointment.scheduled_time || !newAppointment.appointment_type} 
                className="bg-brand-600 hover:bg-brand-700"
              >
                {addAppointmentMutation.isPending ? "Scheduling..." : "Schedule"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={isSendEmailDialogOpen} onOpenChange={setIsSendEmailDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[95vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle>Send Email</DialogTitle><DialogDescription>Send email to {emailData.to_name}.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-4 overflow-y-auto flex-1 min-h-0 pr-2 pl-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label htmlFor="emailTo">To Email *</Label><Input id="emailTo" type="email" value={emailData.to_email} onChange={(e) => setEmailData({...emailData, to_email: e.target.value})} placeholder="customer@example.com"/></div>
              <div className="space-y-2"><Label htmlFor="emailName">To Name *</Label><Input id="emailName" value={emailData.to_name} onChange={(e) => setEmailData({...emailData, to_name: e.target.value})} placeholder="Customer Name"/></div>
            </div>
            <div className="space-y-2"><Label htmlFor="emailTemplate">Template *</Label>
              <Select value={emailData.template_id} onValueChange={handleTemplateChange}>
                <SelectTrigger><SelectValue placeholder="Select template" /></SelectTrigger>
                <SelectContent>{templatesData?.data?.templates?.map((template) => (<SelectItem key={template.id || template._id} value={template.id || template._id}>{template.name} ({template.category})</SelectItem>))}</SelectContent>
              </Select>
            </div>
             {selectedEmailTemplate?.variables?.length > 0 && (
               <div className="space-y-1 border rounded-lg p-2 bg-gray-50 text-xs">
                 <Label className="font-medium">Template Variables:</Label>
                 <div className="flex flex-wrap gap-1">
                   {selectedEmailTemplate.variables.map((v, i) => (<Badge key={i} variant="secondary" className="py-0 px-1">{"{" + v + "}"}</Badge>))}
                 </div>
                 <p className="text-gray-500">Auto-filled by system</p>
               </div>
             )}
            <div className="space-y-2"><Label htmlFor="emailSubject">Subject *</Label><Input id="emailSubject" value={emailData.subject} onChange={(e) => setEmailData({...emailData, subject: e.target.value})} placeholder="Subject"/></div>
            <div className="space-y-2"><Label htmlFor="emailBody">Body *</Label><Textarea id="emailBody" value={emailData.body} onChange={(e) => setEmailData({...emailData, body: e.target.value})} placeholder="Body" rows={6} className="resize-none"/></div>
            
            {/* Email Preview for all HTML emails */}
            {emailData.body && (
              <div className="space-y-2 p-3 bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
                <Label className="text-blue-800 font-medium flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  Email Preview
                </Label>
                <div className="text-sm bg-white p-3 rounded border max-h-48 overflow-y-auto overflow-x-hidden">
                  <div className="font-medium text-gray-700 mb-2 truncate">Subject: {getPreviewContent(emailData.subject)}</div>
                  <div 
                    className="text-gray-600 prose prose-sm max-w-full break-words [&_*]:max-w-full [&_img]:max-w-full [&_table]:max-w-full [&_table]:table-fixed"
                    dangerouslySetInnerHTML={{ __html: getPreviewContent(emailData.body) }}
                  />
                </div>
                {getUnresolvedVariables(getPreviewContent(emailData.subject + emailData.body)).length > 0 && (
                  <div className="text-amber-700 text-xs flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Some variables may not have data: {getUnresolvedVariables(getPreviewContent(emailData.subject + emailData.body)).join(', ')}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSendEmailDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSendEmailSubmit} disabled={sendEmailMutation.isPending || !emailData.subject || !emailData.body || !emailData.to_email || !emailData.to_name} className="bg-brand-600 hover:bg-brand-700">{sendEmailMutation.isPending ? "Sending..." : "Send Email"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Project Dialog */}
       <Dialog open={isCreateProjectDialogOpen} onOpenChange={setIsCreateProjectDialogOpen}>
         <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
           <DialogHeader><DialogTitle>Create New Project</DialogTitle><DialogDescription>For {selectedCustomer?.name}. Info pre-filled.</DialogDescription></DialogHeader>
           <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
             <div className="col-span-2 space-y-2"><Label htmlFor="projectName">Project Name *</Label><Input id="projectName" value={newProject.name} onChange={(e) => setNewProject({...newProject, name: e.target.value})} placeholder="Garden Redesign"/></div>
             <div className="col-span-2 space-y-2"><Label>Customer *</Label><Input value={selectedCustomer ? `${selectedCustomer.last_name ? selectedCustomer.last_name + ', ' : ''}${selectedCustomer.name} (${selectedCustomer.email})` : ''} disabled className="bg-gray-50"/></div>
             <div className="col-span-2 space-y-2"><Label htmlFor="projectType">Type *</Label><Select value={newProject.project_type} onValueChange={(value) => setNewProject({...newProject, project_type: value})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="design_only">Design Only</SelectItem><SelectItem value="construction">Construction</SelectItem></SelectContent></Select></div>
             <div className="col-span-2 space-y-2"><Label htmlFor="jobsiteAddress">Job Address</Label><Input id="jobsiteAddress" value={newProject.jobsite_address} onChange={(e) => setNewProject({...newProject, jobsite_address: e.target.value})} placeholder="123 Main St"/></div>
             <div className="space-y-2"><Label htmlFor="city">City</Label><Input id="city" value={newProject.city || ""} onChange={(e) => setNewProject({...newProject, city: e.target.value})} placeholder="City"/></div>
             <div className="space-y-2"><Label htmlFor="zip">Zip</Label><Input id="zip" value={newProject.zip || ""} onChange={(e) => setNewProject({...newProject, zip: e.target.value})} placeholder="12345"/></div>
             <div className="space-y-2"><Label htmlFor="priority">Priority</Label><Select value={newProject.priority} onValueChange={(value) => setNewProject({...newProject, priority: value})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent></Select></div>
             <div className="space-y-2"><Label htmlFor="contractPrice">Contract Price *</Label><Input id="contractPrice" type="number" value={newProject.contract_price} onChange={(e) => setNewProject({...newProject, contract_price: e.target.value})} placeholder="75000"/></div>
             {newProject.project_type === "design_only" && (<div className="space-y-2"><Label htmlFor="estimatedBudget">Est. Construction Budget</Label><Input id="estimatedBudget" type="number" value={newProject.estimated_construction_budget} onChange={(e) => setNewProject({...newProject, estimated_construction_budget: e.target.value})} placeholder="150000"/></div>)}
             <div className="space-y-2"><Label htmlFor="contractSignDate">Contract Sign Date</Label><Input id="contractSignDate" type="date" value={newProject.contract_sign_date} onChange={(e) => setNewProject({...newProject, contract_sign_date: e.target.value})}/></div>
             <div className="space-y-2"><Label htmlFor="startDate">Start Date</Label><Input id="startDate" type="date" value={newProject.start_date} onChange={(e) => setNewProject({...newProject, start_date: e.target.value})}/></div>
             <div className="space-y-2"><Label htmlFor="endDate">End Date</Label><Input id="endDate" type="date" value={newProject.end_date} onChange={(e) => setNewProject({...newProject, end_date: e.target.value})}/></div>
             <div className="col-span-2 space-y-2"><Label htmlFor="projectDescription">Description</Label><Textarea id="projectDescription" value={newProject.description} onChange={(e) => setNewProject({...newProject, description: e.target.value})} placeholder="Project details..." rows={3}/></div>
              {/* Designer & Subcontractor selection removed — assigned from project view dialog */}
           </div>
           <DialogFooter>
             <Button variant="outline" onClick={() => setIsCreateProjectDialogOpen(false)}>Cancel</Button>
             <Button onClick={handleCreateProject} disabled={createProjectMutation.isPending || !newProject.name || !newProject.contract_price} className="bg-brand-600 hover:bg-brand-700">{createProjectMutation.isPending ? "Creating..." : "Create Project"}</Button>
           </DialogFooter>
         </DialogContent>
       </Dialog>

      {/* Delete Note Confirmation Dialog */}
      <Dialog open={isDeleteNoteDialogOpen} onOpenChange={setIsDeleteNoteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Note</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this note? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteNoteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeleteNote}
              disabled={deleteNoteMutation.isPending}
            >
              {deleteNoteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Contact Log Confirmation Dialog */}
      <Dialog open={isDeleteContactLogDialogOpen} onOpenChange={setIsDeleteContactLogDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Contact Log</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this contact log? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteContactLogDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeleteContactLog}
              disabled={deleteContactLogMutation.isPending}
            >
              {deleteContactLogMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Appointment Confirmation Dialog */}
      <Dialog open={isDeleteAppointmentDialogOpen} onOpenChange={setIsDeleteAppointmentDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Appointment</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingAppointmentTitle}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteAppointmentDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeleteAppointment}
              disabled={deleteAppointmentMutation.isPending}
            >
              {deleteAppointmentMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Appointment Email Preview Dialog */}
      <Dialog open={isAppointmentEmailDialogOpen} onOpenChange={(open) => { 
        if (!open) { 
          setIsAppointmentEmailDialogOpen(false); 
          setAppointmentEmailPreview(null); 
        } 
      }}>
        <DialogContent className="sm:max-w-[650px] p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle className="flex items-center">
              <Mail className="mr-2 h-5 w-5 text-brand-600" />
              Send Appointment Confirmation
            </DialogTitle>
          </DialogHeader>
          
          {appointmentEmailPreview && (
            <div className="px-4">
              <div className="flex items-center gap-4 text-sm mb-1">
                <span className="font-medium text-gray-500">To:</span>
                <span>{appointmentEmailPreview.to_email}</span>
              </div>
              
              <div className="flex items-center gap-4 text-sm mb-3">
                <span className="font-medium text-gray-500">Subject:</span>
                <span className="text-gray-700">{appointmentEmailPreview.subject}</span>
              </div>
              
              <iframe
                srcDoc={appointmentEmailPreview.body}
                title="Email Preview"
                className="w-full border rounded-lg bg-white"
                style={{ height: '480px' }}
              />
            </div>
          )}
          
          <DialogFooter className="p-4 pt-3">
            <Button 
              variant="outline" 
              onClick={() => { setIsAppointmentEmailDialogOpen(false); setAppointmentEmailPreview(null); }}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmSendAppointmentEmail}
              disabled={sendingAppointmentEmail}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {sendingAppointmentEmail ? "Sending..." : "Send Now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Project Detail Dialog */}
      {selectedProjectId && (
        <ProjectDetailDialog
          selectedProject={selectedProjectData}
          isDetailDialogOpen={isProjectDetailOpen}
          setIsDetailDialogOpen={(open) => {
            setIsProjectDetailOpen(open);
            if (!open) {
              setSelectedProjectId(null);
              // Refresh the projects list in case something changed
              queryClient.invalidateQueries({ queryKey: ['customer-projects'] });
            }
          }}
          loadingProjectDetails={loadingProjectDetails}
          designers={[]}
          subcontractors={[]}
          onProjectUpdated={() => {
            queryClient.invalidateQueries({ queryKey: ['customer-projects'] });
            queryClient.invalidateQueries({ queryKey: ['project-detail-customer', selectedProjectId] });
          }}
        />
      )}

    </div>
  );
}

